/**
 * VideoCallPageP2P - Composant de vidéoconférence WebRTC P2P
 * Inspiré de Video-Peers, intégré avec le système existant
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Users, Monitor, MonitorOff, Maximize2, Minimize2, X, Circle, Square, UserX, Crown, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { useSocket } from '../hooks/useSocket';
import { vaultSphereAPI } from '../services/api';
import { API_BASE_URL } from '../config/api';
import peerManager from '../services/peerManager';
import { useMeetingChat } from '../hooks/useMeetingChat';
import ChatWindow from './ChatWindow';

const VideoCallPageP2P = () => {
  const [channelName, setChannelName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inMeeting, setInMeeting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [myStream, setMyStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null); // {id: 'local' | remoteId, type: 'camera' | 'screen'}
  const [remoteStreams, setRemoteStreams] = useState([]); // [{id, stream, userName}]
  const [participants, setParticipants] = useState([]); // [{id, userName}]
  const [copied, setCopied] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false); // Si l'utilisateur est organisateur
  const [forcedMuted, setForcedMuted] = useState(false); // Si l'utilisateur a été forcé à mute
  const [forcedVideoOff, setForcedVideoOff] = useState(false); // Si la vidéo a été forcée à off
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0); // Temps d'enregistrement en secondes
  const [recordingResolution, setRecordingResolution] = useState('1080p'); // Résolution par défaut
  const [showResolutionDialog, setShowResolutionDialog] = useState(false); // Afficher le dialogue de sélection de résolution
  const [chatMinimized, setChatMinimized] = useState(false); // État de minimisation du chat
  const [meetingEventId, setMeetingEventId] = useState(null); // ID du meeting event (pour enregistrement VaultSphere)
  const [insecureContextWarning, setInsecureContextWarning] = useState(false); // Contexte non sécurisé (HTTP) = caméra/micro bloqués

  const localVideoRef = useRef(null);
  const screenShareRef = useRef(null);
  const remoteVideosRef = useRef({});
  const peersRef = useRef(new Set()); // Track des IDs de peers créés
  const timeoutRef = useRef(null); // Référence pour le timeout
  const fallbackTimeoutRef = useRef(null); // Référence pour le fallback timeout
  const originalVideoTracksRef = useRef([]); // Sauvegarder les tracks vidéo originaux
  const mediaRecorderRef = useRef(null); // Référence pour MediaRecorder
  const recordedChunksRef = useRef([]); // Chunks d'enregistrement
  const recordingTimerRef = useRef(null); // Timer pour le temps d'enregistrement
  const canvasRef = useRef(null); // Canvas pour combiner les streams
  const isRecordingRef = useRef(false); // Ref pour suivre l'état d'enregistrement dans updateCanvas
  const handlePeerConnectionFailedRef = useRef(null); // Ref pour éviter dépendance circulaire
  const participantsRef = useRef([]);
  const meetingEventIdRef = useRef(null);
  const token = localStorage.getItem('token');

  // Connexion Socket.io
  const { socket, isConnected } = useSocket(token);

  // Hook pour le chat (réutilise le système existant)
  // Ne pas initialiser le chat tant que la réunion n'a pas démarré
  const chat = useMeetingChat({
    channelName: inMeeting ? channelName : '', // Ne pas initialiser si pas en réunion
    displayName: displayName,
    clientUid: null, // Pas d'UID Agora pour P2P
    client: null,
    dataStream: null,
    onError: (errorMsg) => {
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
    }
  });

  // Ouvrir le chat automatiquement quand on entre dans la réunion
  useEffect(() => {
    if (inMeeting && chat && chat.setChatOpen) {
      chat.setChatOpen(true);
    }
  }, [inMeeting, chat]);

  // Vérifier le contexte sécurisé (HTTPS requis pour caméra/micro)
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isSecure = window.isSecureContext || isLocalhost;
    if (!isSecure) {
      setInsecureContextWarning(true);
      setError('⚠️ Connexion non sécurisée (HTTP). La caméra et le micro nécessitent HTTPS. Utilisez https:// ou localhost pour activer la vidéo/audio.');
    }
  }, []);

  // Récupérer le nom d'utilisateur depuis localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.name || user.email) {
      setDisplayName(user.name || user.email.split('@')[0]);
    }
  }, []);

  participantsRef.current = participants;
  meetingEventIdRef.current = meetingEventId;

  // Vérifier si un room est passé en paramètre URL (pour rejoindre une réunion existante)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    const eventParam = urlParams.get('event'); // ID du meeting event
    
    if (roomParam && !inMeeting && !channelName) {
      console.log('[VideoCallPageP2P] 📥 Room parameter found in URL:', roomParam);
      setChannelName(roomParam);
      if (eventParam) setMeetingEventId(eventParam);
      
      // Si un event ID est fourni, vérifier si l'utilisateur est organisateur
      if (eventParam) {
        checkOrganizerStatus(eventParam);
      }
    }
  }, [inMeeting, channelName, displayName]);

  // Vérifier si l'utilisateur est organisateur du meeting event
  const checkOrganizerStatus = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const API_BASE = API_BASE_URL;
      
      const response = await axios.get(`${API_BASE}/meeting-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const meetingEvent = response.data.data;
        const userId = user._id;
        
        // Vérifier si l'utilisateur est l'organisateur
        if (meetingEvent.organizer && meetingEvent.organizer._id === userId) {
          setIsOrganizer(true);
          console.log('[VideoCallPageP2P] ✅ Utilisateur est organisateur de la réunion');
        }
      }
    } catch (error) {
      console.warn('[VideoCallPageP2P] ⚠️ Erreur vérification organisateur:', error);
    }
  };

  // Générer un nom de channel unique
  const generateChannelName = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `p2p-${timestamp}-${random}`;
  };

  // Obtenir le stream média local avec retry et vérifications améliorées
  const ensureMedia = useCallback(async (requireVideo = true, requireAudio = true, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 500; // 500ms entre les tentatives

    // Vérifier si le stream existant est toujours valide
    if (myStream) {
      const videoTracks = myStream.getVideoTracks();
      const audioTracks = myStream.getAudioTracks();
      const hasValidVideo = !requireVideo || (videoTracks.length > 0 && videoTracks[0].readyState === 'live');
      const hasValidAudio = !requireAudio || (audioTracks.length > 0 && audioTracks[0].readyState === 'live');
      
      if (hasValidVideo && hasValidAudio) {
        console.log('[VideoCallPageP2P] ✅ Stream already exists and is valid, reusing');
        return myStream;
      } else {
        console.log('[VideoCallPageP2P] ⚠️ Existing stream has invalid tracks, getting new stream');
        // Arrêter les tracks invalides
        myStream.getTracks().forEach(track => {
          if (track.readyState !== 'ended') {
            track.stop();
          }
        });
        setMyStream(null);
      }
    }

    try {
      console.log(`[VideoCallPageP2P] 📹 Requesting user media... (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`, { requireVideo, requireAudio });
      
      // Essayer d'abord avec les contraintes idéales
      let constraints = {
        audio: requireAudio ? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : false,
        video: requireVideo ? {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          frameRate: { ideal: 30, min: 15 },
          facingMode: 'user'
        } : false
      };

      // Si c'est un retry, essayer avec des contraintes plus flexibles
      if (retryCount > 0) {
        console.log('[VideoCallPageP2P] 🔄 Retry with more flexible constraints');
        constraints = {
          audio: requireAudio,
          video: requireVideo ? true : false // Contraintes minimales
        };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Vérifier que le stream est valide et que les tracks sont actifs
      const tracks = stream.getTracks();
      const activeTracks = tracks.filter(t => t.readyState === 'live');
      
      if (activeTracks.length === 0) {
        throw new Error('No active tracks in stream');
      }
      
      console.log('[VideoCallPageP2P] ✅ User media obtained successfully', {
        totalTracks: tracks.length,
        activeTracks: activeTracks.length,
        tracks: tracks.map(t => ({ 
          kind: t.kind, 
          enabled: t.enabled, 
          readyState: t.readyState,
          settings: t.getSettings ? {
            width: t.getSettings().width,
            height: t.getSettings().height,
            frameRate: t.getSettings().frameRate
          } : null
        }))
      });
      
      // Vérifier que les tracks vidéo sont vraiment actifs (attendre un peu)
      if (requireVideo) {
        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
          const videoTrack = videoTracks[0];
          if (videoTrack.readyState !== 'live') {
            console.warn('[VideoCallPageP2P] ⚠️ Video track not live after delay, retrying...');
            stream.getTracks().forEach(t => t.stop());
            if (retryCount < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
              return ensureMedia(requireVideo, requireAudio, retryCount + 1);
            }
            throw new Error('Video track not active');
          }
        }
      }
      
      setMyStream(stream);
      
      // Attacher le stream à l'élément vidéo avec plusieurs tentatives
      const attachStreamToVideo = async (attempt = 0) => {
        if (localVideoRef.current) {
          try {
            console.log('[VideoCallPageP2P] 📹 Attaching stream to local video element');
            localVideoRef.current.srcObject = stream;
            
            // Attendre que l'élément soit prêt
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Forcer la lecture avec retry
            try {
              await localVideoRef.current.play();
              console.log('[VideoCallPageP2P] ✅ Local video playing successfully');
            } catch (playError) {
              console.warn('[VideoCallPageP2P] ⚠️ Play error, retrying...', playError);
              if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, 200));
                return attachStreamToVideo(attempt + 1);
              }
              throw playError;
            }
          } catch (err) {
            console.error('[VideoCallPageP2P] ❌ Error attaching stream to video:', err);
            if (attempt < 2) {
              await new Promise(resolve => setTimeout(resolve, 200));
              return attachStreamToVideo(attempt + 1);
            }
          }
        } else {
          // Le ref n'est pas encore disponible, c'est normal au début
          // Ne pas logger comme un warning si c'est juste le premier essai
          if (attempt > 0) {
            console.log(`[VideoCallPageP2P] ⏳ localVideoRef.current is null, retrying... (attempt ${attempt + 1}/3)`);
          }
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 300));
            return attachStreamToVideo(attempt + 1);
          } else {
            // Après 3 tentatives, le ref devrait être disponible
            // Si ce n'est pas le cas, c'est peut-être un problème de timing
            console.warn('[VideoCallPageP2P] ⚠️ localVideoRef.current still null after retries, will be attached when ref is available');
          }
        }
      };
      
      // Essayer d'attacher le stream (avec retry)
      attachStreamToVideo().catch(err => {
        console.error('[VideoCallPageP2P] ❌ Failed to attach stream after retries:', err);
      });
      
      return stream;
    } catch (err) {
      console.error(`[VideoCallPageP2P] ❌ Media error (attempt ${retryCount + 1}):`, err.name, err.message);
      
      // Gestion spéciale pour NotReadableError - arrêter tous les tracks existants avant de réessayer
      if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        console.warn('[VideoCallPageP2P] ⚠️ Camera/microphone already in use, cleaning up existing tracks...');
        
        // Arrêter tous les tracks existants
        if (myStream) {
          myStream.getTracks().forEach(track => {
            if (track.readyState !== 'ended') {
              console.log('[VideoCallPageP2P] 🛑 Stopping existing track:', track.kind, track.id);
              track.stop();
            }
          });
          setMyStream(null);
        }
        
        // Attendre un peu pour que la caméra soit libérée
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Retry une seule fois pour NotReadableError
        if (retryCount === 0) {
          console.log('[VideoCallPageP2P] 🔄 Retrying after cleanup...');
          return ensureMedia(requireVideo, requireAudio, retryCount + 1);
        } else {
          setError('⚠️ La caméra/microphone est déjà utilisée par une autre application. Veuillez la fermer et réessayer.');
          setTimeout(() => setError(''), 8000);
          return null;
        }
      }
      
      // Retry si ce n'est pas une erreur de permission et qu'on n'a pas atteint le max
      if (retryCount < MAX_RETRIES && 
          err.name !== 'NotAllowedError' && 
          err.name !== 'PermissionDeniedError' &&
          err.name !== 'NotFoundError' &&
          err.name !== 'DevicesNotFoundError') {
        console.log(`[VideoCallPageP2P] 🔄 Retrying in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return ensureMedia(requireVideo, requireAudio, retryCount + 1);
      }
      
      // Si l'erreur est une permission refusée ou contexte non sécurisé (HTTP)
      const errMsg = (err.message || '').toLowerCase();
      const isInsecureContext = !window.isSecureContext && (errMsg.includes('insecure') || errMsg.includes('secure') || errMsg.includes('https'));
      if (isInsecureContext || err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (isInsecureContext) {
          setInsecureContextWarning(true);
          setError('🔒 La caméra et le micro nécessitent une connexion HTTPS. Utilisez https:// au lieu de http:// pour activer la vidéo et l\'audio.');
        } else {
          setError('⚠️ Caméra/microphone non disponible. Vous pouvez rejoindre la réunion en mode écoute seule.');
        }
        setTimeout(() => setError(''), 8000);
        return null;
      }
      
      // Pour les autres erreurs, logger et permettre de continuer
      let errorMessage = '⚠️ Caméra/microphone non disponible';
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = '⚠️ Aucune caméra/microphone trouvé';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = '⚠️ Paramètres non supportés';
      }
      
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
      return null;
    }
  }, [myStream]);

  // Ajouter un stream distant avec améliorations pour l'affichage
  const addRemoteStream = useCallback((id, stream, userName) => {
    if (!stream) {
      console.warn('[VideoCallPageP2P] ⚠️ Cannot add remote stream: stream is null/undefined', { id, userName });
      return;
    }

    // Vérifier que le stream a des tracks actifs
    const tracks = stream.getTracks();
    const activeTracks = tracks.filter(t => t.readyState === 'live');
    
    if (activeTracks.length === 0) {
      console.warn('[VideoCallPageP2P] ⚠️ Remote stream has no active tracks, waiting...', { id, userName });
      // Attendre un peu et réessayer
      setTimeout(() => {
        const newTracks = stream.getTracks();
        const newActiveTracks = newTracks.filter(t => t.readyState === 'live');
        if (newActiveTracks.length > 0) {
          console.log('[VideoCallPageP2P] ✅ Remote stream tracks are now active, adding...', { id });
          addRemoteStream(id, stream, userName);
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ Remote stream still has no active tracks after wait', { id });
        }
      }, 500);
      return;
    }

    console.log('[VideoCallPageP2P] ✅ Adding remote stream:', { 
      id, 
      userName, 
      hasStream: !!stream, 
      totalTracks: tracks.length,
      activeTracks: activeTracks.length,
      tracks: tracks.map(t => ({ 
        kind: t.kind, 
        enabled: t.enabled, 
        readyState: t.readyState 
      }))
    });
    
    setRemoteStreams((prev) => {
      const exists = prev.find((s) => s.id === id);
      if (exists) {
        // Mettre à jour le stream existant
        console.log('[VideoCallPageP2P] 🔄 Updating existing remote stream:', id);
        return prev.map((s) => (s.id === id ? { id, stream, userName } : s));
      }
      // Ajouter le nouveau stream
      console.log('[VideoCallPageP2P] ➕ Adding new remote stream:', id);
      return [...prev, { id, stream, userName }];
    });
    
    // Mettre à jour l'élément vidéo avec retry logic
    const attachStreamToVideoElement = async (attempt = 0) => {
      const videoElement = remoteVideosRef.current[id];
      
      if (!videoElement) {
        if (attempt < 5) {
          // L'élément n'existe pas encore, attendre un peu et réessayer
          console.log(`[VideoCallPageP2P] ⏳ Video element not ready for ${id}, retrying... (attempt ${attempt + 1}/5)`);
          await new Promise(resolve => setTimeout(resolve, 200));
          return attachStreamToVideoElement(attempt + 1);
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ Video element not found after retries:', id);
          return;
        }
      }

      if (stream && videoElement.srcObject !== stream) {
        try {
          console.log('[VideoCallPageP2P] 📹 Attaching remote stream to video element:', id);
          
          // Vérifier que le stream a toujours des tracks actifs
          const currentTracks = stream.getTracks();
          const currentActiveTracks = currentTracks.filter(t => t.readyState === 'live');
          
          if (currentActiveTracks.length === 0) {
            console.warn('[VideoCallPageP2P] ⚠️ Stream has no active tracks when trying to attach:', id);
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 500));
              return attachStreamToVideoElement(attempt + 1);
            }
            return;
          }
          
          videoElement.srcObject = stream;
          
          // Attendre un peu pour que l'élément soit prêt
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Essayer de jouer avec retry
          try {
            await videoElement.play();
            console.log('[VideoCallPageP2P] ✅ Remote video playing successfully:', id);
          } catch (playError) {
            console.warn('[VideoCallPageP2P] ⚠️ Play error for remote video:', id, playError.name);
            if (attempt < 3 && playError.name !== 'NotAllowedError') {
              await new Promise(resolve => setTimeout(resolve, 300));
              return attachStreamToVideoElement(attempt + 1);
            }
            // Ne pas throw, juste logger
          }
        } catch (err) {
          console.error('[VideoCallPageP2P] ❌ Error attaching remote stream:', id, err);
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 300));
            return attachStreamToVideoElement(attempt + 1);
          }
        }
      } else if (videoElement.srcObject === stream) {
        console.log('[VideoCallPageP2P] ✅ Stream already attached to video element:', id);
        // S'assurer que la vidéo joue
        if (videoElement.paused) {
          videoElement.play().catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              console.warn('[VideoCallPageP2P] ⚠️ Error playing already attached video:', err.name);
            }
          });
        }
      }
    };
    
    // Démarrer l'attachement avec un petit délai pour laisser le DOM se mettre à jour
    setTimeout(() => {
      attachStreamToVideoElement().catch(err => {
        console.error('[VideoCallPageP2P] ❌ Failed to attach remote stream after retries:', id, err);
      });
    }, 100);
  }, []);

  // Supprimer un stream distant
  const removeRemoteStream = useCallback((id) => {
    setRemoteStreams((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Gérer les échecs de connexion peer (utilise une ref pour éviter dépendance circulaire)
  const handlePeerConnectionFailed = useCallback((remoteId) => {
    console.warn(`[VideoCallPageP2P] ⚠️ Peer connection failed with ${remoteId}, attempting to reconnect...`);
    
    const peer = peerManager.peers?.get(remoteId);
    if (!peer) {
      console.warn(`[VideoCallPageP2P] ⚠️ Peer not found for ${remoteId}, cannot reconnect`);
      return;
    }

    // Vérifier l'état ICE
    const iceState = peer.iceConnectionState;
    const connectionState = peer.connectionState;
    
    console.log(`[VideoCallPageP2P] 📊 Connection diagnostics for ${remoteId}:`, {
      connectionState,
      iceConnectionState: iceState,
      signalingState: peer.signalingState,
      hasLocalDescription: !!peer.localDescription,
      hasRemoteDescription: !!peer.remoteDescription,
      senders: peer.getSenders().length,
      receivers: peer.getReceivers().length
    });

    // Si la connexion a échoué, essayer de recréer la connexion
    if (connectionState === 'failed' || iceState === 'failed') {
      // Cas spécial : ICE connecté mais connexion échouée - peut-être un problème de tracks
      if (iceState === 'connected' && connectionState === 'failed') {
        console.warn(`[VideoCallPageP2P] ⚠️ ICE connected but connection failed - attempting to restore tracks...`);
        
        // Essayer de restaurer les tracks avant de recréer la connexion
        const peer = peerManager.peers?.get(remoteId);
        if (peer && myStream) {
          const senders = peer.getSenders();
          const activeTracks = myStream.getTracks().filter(t => t.readyState === 'live');
          
          // Vérifier si on a des tracks actifs mais pas de senders actifs
          if (activeTracks.length > 0 && senders.length === 0) {
            console.log(`[VideoCallPageP2P] 🔧 Restoring tracks to peer connection...`);
            try {
              peerManager.addTracks(remoteId, myStream);
              // Attendre un peu et vérifier si ça a aidé (utiliser Promise sans await)
              new Promise(resolve => setTimeout(resolve, 500)).then(() => {
                // Si la connexion est toujours échouée, recréer
                if (peer.connectionState === 'failed') {
                  console.warn(`[VideoCallPageP2P] ⚠️ Track restoration didn't help, will recreate connection...`);
                  // Continuer avec la recréation de la connexion
                } else {
                  console.log(`[VideoCallPageP2P] ✅ Connection restored after track restoration`);
                  return; // Ne pas recréer la connexion
                }
              }).catch(restoreError => {
                console.error(`[VideoCallPageP2P] ❌ Error restoring tracks:`, restoreError);
              });
            } catch (restoreError) {
              console.error(`[VideoCallPageP2P] ❌ Error restoring tracks:`, restoreError);
            }
          }
        }
      }
      
      // Trouver le participant pour récupérer son nom
      const participant = participants.find(p => p.id === remoteId);
      const userName = participant?.userName;
      
      // Fermer l'ancienne connexion
      peerManager.closePeer(remoteId);
      peersRef.current.delete(remoteId);
      removeRemoteStream(remoteId);
      
      // Attendre un peu avant de reconnecter
      setTimeout(async () => {
        console.log(`[VideoCallPageP2P] 🔄 Attempting to reconnect to ${remoteId}...`);
        try {
          // Utiliser la ref pour éviter la dépendance circulaire
          if (handlePeerConnectionFailedRef.current) {
            await handlePeerConnectionFailedRef.current(remoteId, true, userName);
          }
        } catch (error) {
          console.error(`[VideoCallPageP2P] ❌ Failed to reconnect to ${remoteId}:`, error);
          setError(`⚠️ Impossible de se reconnecter à ${userName || remoteId.slice(-4)}. Vérifiez votre connexion réseau.`);
          setTimeout(() => setError(''), 5000);
        }
      }, 2000); // Attendre 2 secondes avant de reconnecter
    }
  }, [participants, removeRemoteStream]);

  // Configurer une connexion peer
  const setupPeer = useCallback(async (remoteId, isInitiator, userName = null) => {
    if (!socket) {
      console.warn('[VideoCallPageP2P] ⚠️ Socket not available for setupPeer');
      return;
    }
    
    if (peersRef.current.has(remoteId)) {
      console.log('[VideoCallPageP2P] ✅ Peer already exists for:', remoteId);
      return;
    }

    try {
      console.log('[VideoCallPageP2P] 🔗 Setting up peer connection:', { remoteId, isInitiator, userName });
      
      // Utiliser le stream existant si disponible, sinon essayer d'en obtenir un
      let stream = myStream;
      if (!stream) {
        try {
          stream = await ensureMedia();
        } catch (mediaError) {
          console.warn('[VideoCallPageP2P] ⚠️ Could not get media stream, continuing without local video/audio:', mediaError);
          // Continuer sans stream local - l'utilisateur pourra toujours recevoir les streams distants
        }
      }

      // Créer la connexion peer avec gestion des échecs
      const peer = peerManager.createPeer(
        remoteId,
        (id, remoteStream) => {
          console.log('[VideoCallPageP2P] ✅✅✅ Received stream from:', id, 'Stream tracks:', remoteStream.getTracks().map(t => t.kind));
          addRemoteStream(id, remoteStream, userName || `User ${id.slice(-4)}`);
        },
        (id, candidate) => {
          // Envoyer le candidat ICE via Socket.io
          console.log('[VideoCallPageP2P] 📤 Sending ICE candidate to:', id);
          socket.emit('p2p:ice-candidate', {
            to: id,
            candidate: candidate.toJSON(),
            roomId: channelName
          });
        },
        (id) => {
          // Callback appelé quand la connexion échoue
          if (handlePeerConnectionFailedRef.current) {
            handlePeerConnectionFailedRef.current(id);
          }
        }
      );

      peersRef.current.add(remoteId);

      // Ajouter les tracks locaux seulement si on a un stream
      // IMPORTANT: Ajouter les tracks AVANT de créer l'offre pour qu'ils soient inclus
      if (stream) {
        // Vérifier que le stream est valide et que les tracks sont actifs
        const tracks = stream.getTracks();
        const activeTracks = tracks.filter(t => t.readyState === 'live');
        
        if (activeTracks.length === 0) {
          console.warn('[VideoCallPageP2P] ⚠️ Stream has no active tracks, cannot add to peer');
        } else {
          console.log('[VideoCallPageP2P] 📹 Adding local tracks to peer:', remoteId, {
            totalTracks: tracks.length,
            activeTracks: activeTracks.length,
            tracks: tracks.map(t => ({ 
              kind: t.kind, 
              enabled: t.enabled, 
              readyState: t.readyState 
            }))
          });
          
          peerManager.addTracks(remoteId, stream);
          
          // Vérifier que les tracks ont bien été ajoutés avec retry
          let tracksAdded = false;
          for (let i = 0; i < 3; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
            const peer = peerManager.peers?.get(remoteId);
            if (peer) {
              const senders = peer.getSenders();
              const trackKinds = senders.map(s => s.track?.kind).filter(Boolean);
              console.log('[VideoCallPageP2P] ✅ Peer senders after adding tracks (attempt ' + (i + 1) + '):', trackKinds);
              
              // Vérifier qu'on a au moins les tracks attendus
              const expectedVideo = stream.getVideoTracks().length > 0;
              const expectedAudio = stream.getAudioTracks().length > 0;
              const hasVideo = trackKinds.includes('video');
              const hasAudio = trackKinds.includes('audio');
              
              if ((!expectedVideo || hasVideo) && (!expectedAudio || hasAudio)) {
                tracksAdded = true;
                break;
              }
            }
          }
          
          if (!tracksAdded) {
            console.warn('[VideoCallPageP2P] ⚠️ Tracks may not have been added correctly, but continuing...');
          }
        }
      } else {
        console.log('[VideoCallPageP2P] ⚠️ No local stream available, peer connection will be receive-only');
      }

      // Si initiateur, créer une offre APRÈS avoir ajouté les tracks
      if (isInitiator) {
        // Attendre un peu plus pour s'assurer que les tracks sont bien ajoutés et que la connexion est prête
        await new Promise(resolve => setTimeout(resolve, 200));
        
        console.log('[VideoCallPageP2P] 📤 Creating offer for:', remoteId);
        const offer = await peerManager.createOffer(remoteId);
        if (offer) {
          // Vérifier que l'offre a bien été créée et que l'état est correct
          const peer = peerManager.peers?.get(remoteId);
          if (peer && peer.signalingState !== 'have-local-offer') {
            console.warn('[VideoCallPageP2P] ⚠️ Offer created but signaling state is not have-local-offer:', peer.signalingState);
          }
          // Vérifier que l'offre contient bien les tracks (en vérifiant le SDP)
          const hasVideo = offer.sdp?.includes('m=video');
          const hasAudio = offer.sdp?.includes('m=audio');
          const expectedVideo = stream && stream.getVideoTracks().length > 0;
          const expectedAudio = stream && stream.getAudioTracks().length > 0;
          
          console.log('[VideoCallPageP2P] 📤 Offer created:', {
            hasVideo,
            hasAudio,
            expectedVideo,
            expectedAudio,
            sdpPreview: offer.sdp?.substring(0, 200)
          });
          
          if ((expectedVideo && !hasVideo) || (expectedAudio && !hasAudio)) {
            console.warn('[VideoCallPageP2P] ⚠️ Offer may be missing tracks, but sending anyway...');
          }
          
          socket.emit('p2p:offer', {
            to: remoteId,
            offer: offer,
            roomId: channelName,
            userName: displayName
          });
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ Failed to create offer for:', remoteId);
        }
      }
    } catch (error) {
      console.error('[VideoCallPageP2P] ❌ Error setting up peer:', error);
      setError('Erreur lors de la création de la connexion: ' + error.message);
    }
  }, [socket, myStream, ensureMedia, addRemoteStream, channelName, displayName]);

  // Mettre à jour la ref pour éviter la dépendance circulaire
  useEffect(() => {
    handlePeerConnectionFailedRef.current = setupPeer;
  }, [setupPeer]);

  // Gérer l'arrivée d'un utilisateur
  const handleUserJoined = useCallback(async ({ id, userName, userId: joinedUserId, members }) => {
    console.log('[VideoCallPageP2P] 👤 User joined:', { id, userName, joinedUserId, members });
    
    // Mettre à jour la liste des participants (avec userId pour partage enregistrements)
    setParticipants((prev) => {
      const next = new Map(prev.map((p) => [p.id, p]));
      if (id) {
        next.set(id, { id, userName: userName || `User ${id.slice(-4)}`, userId: joinedUserId || members?.find(m => m.id === id)?.userId || null });
      }
      (members || []).forEach((m) => {
        next.set(m.id, { id: m.id, userName: m.userName || `User ${m.id.slice(-4)}`, userId: m.userId || null });
      });
      return Array.from(next.values());
    });

    // Créer une connexion peer (les membres existants initient vers le nouveau venu)
    // Le nouveau venu recevra l'offre et créera une réponse
    if (id && id !== socket?.id) {
      console.log('[VideoCallPageP2P] 🔗 Setting up peer connection to new user:', id);
      await setupPeer(id, true, userName);
    } else {
      console.warn('[VideoCallPageP2P] ⚠️ Ignoring user joined event for self or invalid ID:', id);
    }
  }, [setupPeer, socket]);

  // Gérer les membres existants lors de la connexion
  const handleExistingMembers = useCallback(async (members = []) => {
    console.log('[VideoCallPageP2P] 👥 Existing members:', members);
    
    setParticipants((prev) => {
      const next = new Map(prev.map((p) => [p.id, p]));
      members.forEach((m) => {
        if (m.id !== socket?.id) { // Ne pas s'ajouter soi-même
          next.set(m.id, { id: m.id, userName: m.userName || `User ${m.id.slice(-4)}`, userId: m.userId || null });
        }
      });
      return Array.from(next.values());
    });

    // Créer des connexions avec tous les membres existants
    // Le nouveau venu initie les connexions vers les membres existants
    for (const member of members) {
      if (member.id !== socket?.id) { // Ne pas créer de connexion avec soi-même
        console.log('[VideoCallPageP2P] 🔗 Setting up peer connection to existing member:', member.id);
        await setupPeer(member.id, true, member.userName);
      }
    }
  }, [setupPeer, socket]);

  // Gérer une offre reçue
  const handleOffer = useCallback(async ({ from, offer, userName }) => {
    console.log('[VideoCallPageP2P] 📥 Received offer from:', from);
    
    // Créer la connexion peer si elle n'existe pas (en tant que récepteur, pas initiateur)
    await setupPeer(from, false, userName);
    
    const peer = peerManager.peers?.get(from);
    if (!peer) {
      console.warn('[VideoCallPageP2P] ⚠️ Peer connection not found for:', from);
      return;
    }
    
    // Vérifier l'état de la connexion
    console.log('[VideoCallPageP2P] 📊 Peer signaling state:', peer.signalingState);
    
    // Si on a déjà une offre locale en attente, on a un conflit (glare condition)
    // Les deux pairs ont créé une offre en même temps
    if (peer.signalingState === 'have-local-offer') {
      console.warn('[VideoCallPageP2P] ⚠️ Glare condition: both peers created offers simultaneously');
      console.warn('[VideoCallPageP2P] ⚠️ Current state:', {
        signalingState: peer.signalingState,
        hasLocalDescription: !!peer.localDescription,
        hasRemoteDescription: !!peer.remoteDescription
      });
      
      // Pour résoudre la glare condition, on doit annuler notre offre locale
      // et créer une réponse à l'offre reçue
      try {
        console.log('[VideoCallPageP2P] 🔄 Attempting rollback to resolve glare condition');
        await peer.setLocalDescription({ type: 'rollback' });
        console.log('[VideoCallPageP2P] ✅ Rollback successful, now in state:', peer.signalingState);
      } catch (rollbackError) {
        console.warn('[VideoCallPageP2P] ⚠️ Rollback not supported or failed:', rollbackError.name);
        // Si rollback n'est pas supporté, fermer et recréer la connexion
        console.log('[VideoCallPageP2P] 🔄 Closing and recreating peer connection to resolve glare');
        peerManager.closePeer(from);
        await setupPeer(from, false, userName);
        // Récupérer la nouvelle connexion
        const newPeer = peerManager.peers?.get(from);
        if (!newPeer) {
          console.error('[VideoCallPageP2P] ❌ Failed to recreate peer connection');
          return;
        }
        // Continuer avec la nouvelle connexion
      }
    }
    
    // S'assurer que les tracks locaux sont ajoutés avant de créer la réponse
    if (myStream) {
      console.log('[VideoCallPageP2P] 📹 Ensuring local tracks are added before creating answer');
      
      // Vérifier quels tracks sont déjà présents
      const existingSenders = peer.getSenders();
      const existingVideoTracks = existingSenders.filter(s => s.track?.kind === 'video').map(s => s.track?.id).filter(Boolean);
      const existingAudioTracks = existingSenders.filter(s => s.track?.kind === 'audio').map(s => s.track?.id).filter(Boolean);
      
      // Ajouter les tracks vidéo manquants
      myStream.getVideoTracks().forEach(track => {
        if (!existingVideoTracks.includes(track.id)) {
          console.log('[VideoCallPageP2P] 📹 Adding video track to peer:', from, track.id);
          peer.addTrack(track, myStream);
        }
      });
      
      // Ajouter les tracks audio manquants
      myStream.getAudioTracks().forEach(track => {
        if (!existingAudioTracks.includes(track.id)) {
          console.log('[VideoCallPageP2P] 🔊 Adding audio track to peer:', from, track.id);
          peer.addTrack(track, myStream);
        }
      });
      
      // Attendre un peu pour s'assurer que les tracks sont bien ajoutés
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Vérifier l'état de la connexion avant de créer une réponse
    const currentPeerState = peerManager.peers?.get(from);
    if (currentPeerState) {
      // Si la connexion est déjà stable, on n'a pas besoin de créer une réponse
      if (currentPeerState.signalingState === 'stable' && currentPeerState.remoteDescription) {
        console.log('[VideoCallPageP2P] ℹ️ Connection is already stable, answer not needed');
        return;
      }
      
      // Si la connexion est disconnected/failed, ne pas essayer de créer une réponse
      if (currentPeerState.connectionState === 'disconnected' || currentPeerState.connectionState === 'failed' ||
          currentPeerState.iceConnectionState === 'disconnected' || currentPeerState.iceConnectionState === 'failed') {
        console.warn('[VideoCallPageP2P] ⚠️ Connection is disconnected/failed, cannot create answer');
        console.warn('[VideoCallPageP2P] 📊 Connection state:', currentPeerState.connectionState, 'ICE state:', currentPeerState.iceConnectionState);
        return;
      }
    }
    
    // Créer une réponse
    console.log('[VideoCallPageP2P] 📤 Creating answer for:', from);
    
    try {
      const answer = await peerManager.createAnswer(from, offer);
      if (answer) {
        console.log('[VideoCallPageP2P] 📤 Sending answer to:', from);
        socket.emit('p2p:answer', {
          to: from,
          answer: answer,
          roomId: channelName,
          userName: displayName
        });
      } else {
        console.warn('[VideoCallPageP2P] ⚠️ Failed to create answer for:', from);
        
        // Vérifier l'état de la connexion pour diagnostiquer le problème
        const peer = peerManager.peers?.get(from);
        if (peer) {
          console.warn('[VideoCallPageP2P] 📊 Peer state when answer creation failed:', {
            signalingState: peer.signalingState,
            connectionState: peer.connectionState,
            iceConnectionState: peer.iceConnectionState,
            hasLocalDescription: !!peer.localDescription,
            hasRemoteDescription: !!peer.remoteDescription,
            sendersCount: peer.getSenders().length,
            receiversCount: peer.getReceivers().length
          });
          
          // Si on est en état stable et qu'on a déjà une description, c'est peut-être une re-négociation
          if (peer.signalingState === 'stable' && peer.remoteDescription) {
            console.log('[VideoCallPageP2P] ℹ️ Connection is stable, answer creation may not be needed');
          } else if (peer.signalingState === 'have-local-offer') {
            console.warn('[VideoCallPageP2P] ⚠️ Still have local offer, may need to wait or recreate connection');
          }
        }
      }
    } catch (error) {
      console.error('[VideoCallPageP2P] ❌ Error creating answer for:', from, error.name, error.message);
      
      // Si l'erreur est récupérable, essayer de recréer la connexion
      if (error.name === 'InvalidStateError' || error.name === 'InvalidAccessError') {
        console.log('[VideoCallPageP2P] 🔄 Attempting to recover by recreating peer connection');
        // Fermer la connexion existante
        peerManager.closePeer(from);
        // Recréer la connexion
        setTimeout(async () => {
          try {
            await setupPeer(from, false);
            const newAnswer = await peerManager.createAnswer(from, offer);
            if (newAnswer && socket) {
              socket.emit('p2p:answer', {
                to: from,
                answer: newAnswer,
                roomId: channelName,
                userName: displayName
              });
            }
          } catch (recoveryError) {
            console.error('[VideoCallPageP2P] ❌ Recovery attempt failed:', recoveryError);
          }
        }, 500);
      }
    }
  }, [setupPeer, socket, channelName, displayName, myStream]);

  // Gérer une réponse reçue
  const handleAnswer = useCallback(async ({ from, answer }) => {
    console.log('[VideoCallPageP2P] 📥 Received answer from:', from);
    
    // Vérifier l'état de la connexion avant de traiter la réponse
    const peer = peerManager.peers?.get(from);
    if (peer) {
      console.log('[VideoCallPageP2P] 📊 Peer state before handling answer:', {
        signalingState: peer.signalingState,
        hasLocalDescription: !!peer.localDescription,
        hasRemoteDescription: !!peer.remoteDescription
      });
      
      // Si on est en état have-remote-offer, on ne peut pas définir une réponse distante
      if (peer.signalingState === 'have-remote-offer') {
        console.warn('[VideoCallPageP2P] ⚠️ Cannot handle answer: in have-remote-offer state (glare condition)');
        console.warn('[VideoCallPageP2P] ⚠️ This answer will be ignored - we need to create an answer to the received offer instead');
        return; // Ignorer cette réponse
      }
    }
    
    try {
      await peerManager.setRemoteAnswer(from, answer);
    } catch (error) {
      // Gérer les erreurs InvalidStateError (réponse déjà définie)
      if (error.name === 'InvalidStateError') {
        const peer = peerManager.peers?.get(from);
        if (peer && peer.signalingState === 'stable') {
          console.log('[VideoCallPageP2P] ℹ️ Answer already set, connection is stable');
          return; // C'est normal, ignorer
        }
      }
      
      // Gérer les erreurs InvalidStateError spécifiquement pour have-remote-offer
      if (error.name === 'InvalidStateError' && error.message.includes('have-remote-offer')) {
        const peer = peerManager.peers?.get(from);
        if (peer && peer.signalingState === 'have-remote-offer') {
          console.warn('[VideoCallPageP2P] ⚠️ Received answer while in have-remote-offer state (glare condition)');
          console.warn('[VideoCallPageP2P] ⚠️ Both peers created offers - ignoring this answer');
          console.warn('[VideoCallPageP2P] ⚠️ Will handle the received offer instead');
          return; // Ignorer cette réponse, on va créer notre propre réponse à l'offre reçue
        }
      }
      
      // Gérer les erreurs InvalidAccessError liées au rôle SSL/DTLS
      if (error.name === 'InvalidAccessError' && error.message.includes('SSL role')) {
        console.warn('[VideoCallPageP2P] ⚠️ SSL role conflict detected, attempting to recover');
        const peer = peerManager.peers?.get(from);
        if (peer) {
          console.log('[VideoCallPageP2P] Current peer state:', {
            signalingState: peer.signalingState,
            connectionState: peer.connectionState,
            iceConnectionState: peer.iceConnectionState
          });
          
          // Si la connexion ICE est déjà établie, on peut continuer malgré l'erreur SDP
          if (peer.iceConnectionState === 'connected' || peer.iceConnectionState === 'completed') {
            console.log('[VideoCallPageP2P] ℹ️ ICE is connected, connection may still work despite SDP error');
            return;
          }
        }
      }
      
      console.error('[VideoCallPageP2P] ❌ Error handling answer from:', from, error.name, error.message);
    }
  }, []);

  // Gérer un candidat ICE reçu
  const handleIceCandidate = useCallback(async ({ from, candidate }) => {
    console.log('[VideoCallPageP2P] Received ICE candidate from:', from);
    // S'assurer que la connexion peer existe
    if (!peerManager.hasPeer(from)) {
      await setupPeer(from, false);
    }
    await peerManager.addIceCandidate(from, candidate);
  }, [setupPeer]);

  // Gérer le départ d'un participant
  const handleParticipantLeft = useCallback((id) => {
    console.log('[VideoCallPageP2P] Participant left:', id);
    peerManager.closePeer(id);
    peersRef.current.delete(id);
    removeRemoteStream(id);
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  }, [removeRemoteStream]);

  // Quitter la réunion (défini avant handleControlReceived pour éviter l'erreur d'initialisation)
  const leaveMeeting = useCallback(() => {
    if (socket && channelName) {
      socket.emit('p2p:room:leave', { roomId: channelName });
    }

    // Fermer toutes les connexions peer
    peerManager.closeAll();
    peersRef.current.clear();

    // Arrêter le stream local
    if (myStream) {
      myStream.getTracks().forEach((track) => track.stop());
      setMyStream(null);
    }

    // Arrêter le partage d'écran si actif
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
    }

    // Arrêter l'enregistrement si actif (appel direct sans dépendance pour éviter erreur d'initialisation)
    if (isRecording && mediaRecorderRef.current) {
      isRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }

    // Réinitialiser les états
    setRemoteStreams([]);
    setParticipants([]);
    setInMeeting(false);
    setChannelName('');
    originalVideoTracksRef.current = [];
  }, [socket, channelName, myStream, screenStream]);

  // Gérer les contrôles reçus de l'organisateur
  const handleControlReceived = useCallback(({ action, from, roomId }) => {
    console.log('[VideoCallPageP2P] 🎛️ Control received:', action);
    
    switch (action) {
      case 'mute':
        if (myStream) {
          myStream.getAudioTracks().forEach(track => {
            track.enabled = false;
          });
          setIsAudioEnabled(false);
          setForcedMuted(true);
          setError('🔇 L\'organisateur a désactivé votre micro');
          setTimeout(() => setError(''), 5000);
        }
        break;
      case 'unmute':
        // L'organisateur ne peut plus activer le micro des autres utilisateurs
        // (liberté personnelle - l'utilisateur doit activer son propre micro)
        console.log('[VideoCallPageP2P] ℹ️ Unmute command received but ignored - users must unmute themselves');
        break;
      case 'disable-video':
        if (myStream) {
          myStream.getVideoTracks().forEach(track => {
            track.enabled = false;
          });
          setIsVideoEnabled(false);
          setForcedVideoOff(true);
          setError('📹 L\'organisateur a désactivé votre caméra. Vous pouvez la réactiver si nécessaire.');
          setTimeout(() => setError(''), 5000);
        }
        break;
      case 'enable-video':
        // L'organisateur ne peut plus activer la caméra des autres utilisateurs
        // (liberté personnelle - l'utilisateur doit activer sa propre caméra)
        console.log('[VideoCallPageP2P] ℹ️ Enable-video command received but ignored - users must enable their own camera');
        break;
      case 'kick':
        setError('🚪 Vous avez été expulsé de la réunion par l\'organisateur');
        setTimeout(() => {
          leaveMeeting();
          window.location.href = '/';
        }, 2000);
        break;
    }
  }, [myStream, leaveMeeting]);

  // Gérer les notifications de contrôle des participants
  const handleParticipantControlled = useCallback(({ targetId, action, by }) => {
    console.log('[VideoCallPageP2P] 👤 Participant controlled:', { targetId, action, by });
    // Optionnel : afficher une notification dans le chat
  }, []);

  // Fonctions de contrôle organisateur
  const muteParticipant = useCallback((participantId) => {
    if (!socket || !channelName || !isOrganizer) return;
    
    socket.emit('p2p:control:participant', {
      action: 'mute',
      targetId: participantId,
      roomId: channelName
    });
  }, [socket, channelName, isOrganizer]);

  // Retiré : L'organisateur ne peut plus activer le micro des autres utilisateurs
  // (liberté personnelle - l'utilisateur doit activer son propre micro)

  const disableVideoParticipant = useCallback((participantId) => {
    if (!socket || !channelName || !isOrganizer) return;
    
    socket.emit('p2p:control:participant', {
      action: 'disable-video',
      targetId: participantId,
      roomId: channelName
    });
  }, [socket, channelName, isOrganizer]);

  // Retiré : L'organisateur ne peut plus activer la caméra des autres utilisateurs
  // (liberté personnelle - l'utilisateur doit activer sa propre caméra)

  const kickParticipant = useCallback((participantId) => {
    if (!socket || !channelName || !isOrganizer) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir expulser ce participant ?')) {
      socket.emit('p2p:control:participant', {
        action: 'kick',
        targetId: participantId,
        roomId: channelName
      });
    }
  }, [socket, channelName, isOrganizer]);

  // Handler pour room:joined - doit être stable pour éviter les réexécutions
  const handleRoomJoined = useCallback(({ members, userName, roomId }) => {
    console.log('[VideoCallPageP2P] ✅✅✅✅✅ Room joined event received!', { members, userName, roomId });
    setDisplayName((prev) => userName || prev);
    
    // S'assurer que channelName est défini avec le roomId du serveur
    if (roomId) {
      console.log('[VideoCallPageP2P] 📝 Setting channelName from server roomId:', roomId);
      setChannelName(roomId);
      
      // Si l'utilisateur est le premier à rejoindre (pas de membres existants), il devient organisateur
      if (members.length === 0 && !isOrganizer) {
        console.log('[VideoCallPageP2P] 👑 Premier participant, devient organisateur');
        setIsOrganizer(true);
        // Notifier le serveur
        if (socket) {
          socket.emit('p2p:room:set-organizer', {
            roomId: roomId,
            isOrganizer: true
          });
        }
      }
    }
    
    // Nettoyer les timeouts si présents
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
      console.log('[VideoCallPageP2P] 🧹 Fallback timeout cleaned up');
    }
    
    // Passer en mode réunion une fois la confirmation reçue
    console.log('[VideoCallPageP2P] 🔄 Setting inMeeting to true (from server event)');
    setInMeeting((current) => {
      if (!current) {
        console.log('[VideoCallPageP2P] ✅✅✅✅✅ inMeeting changed from false to true');
      }
      return true;
    });
    setLoading(false); // Arrêter le chargement
    handleExistingMembers(members);
  }, [handleExistingMembers]);

  // Handler pour les erreurs Socket.io
  const handleSocketError = useCallback((error) => {
    console.error('[VideoCallPageP2P] ❌ Socket error:', error);
    setError(error.message || 'Erreur de connexion');
    setLoading(false);
  }, []);

  // Écouter les événements Socket.io - SEULEMENT quand le socket change
  useEffect(() => {
    if (!socket) {
      console.log('[VideoCallPageP2P] ⚠️ Socket not available in useEffect');
      return;
    }

    console.log('[VideoCallPageP2P] 📡 Setting up Socket.io event listeners');
    console.log('[VideoCallPageP2P] 📡 Socket ID:', socket.id);
    console.log('[VideoCallPageP2P] 📡 Socket connected:', socket.connected);

    // Écouter l'événement avec plusieurs méthodes pour être sûr
    console.log('[VideoCallPageP2P] 📡 Registering p2p:room:joined listener');
    socket.on('p2p:room:joined', handleRoomJoined);
    
    // Écouter aussi avec once pour debug
    socket.once('p2p:room:joined', (data) => {
      console.log('[VideoCallPageP2P] ✅✅✅ ONCE handler triggered!', data);
    });
    
    // Écouter tous les événements pour debug
    const allEventsHandler = (eventName, ...args) => {
      console.log('[VideoCallPageP2P] 🟢 Socket event received:', eventName, args);
    };
    socket.onAny(allEventsHandler);

    // Écouter les erreurs du serveur
    socket.on('error', handleSocketError);

    socket.on('p2p:user:joined', handleUserJoined);
    socket.on('p2p:offer', handleOffer);
    socket.on('p2p:answer', handleAnswer);
    socket.on('p2p:ice-candidate', handleIceCandidate);
    socket.on('p2p:user:left', ({ id }) => handleParticipantLeft(id));

    // Écouter les contrôles de l'organisateur
    socket.on('p2p:control:received', handleControlReceived);
    socket.on('p2p:participant:controlled', handleParticipantControlled);

    return () => {
      console.log('[VideoCallPageP2P] 🧹 Cleaning up Socket.io event listeners');
      socket.off('p2p:room:joined', handleRoomJoined);
      socket.off('error', handleSocketError);
      socket.off('p2p:user:joined', handleUserJoined);
      socket.off('p2p:offer', handleOffer);
      socket.off('p2p:answer', handleAnswer);
      socket.off('p2p:ice-candidate', handleIceCandidate);
      socket.off('p2p:user:left', handleParticipantLeft);
      socket.off('p2p:control:received', handleControlReceived);
      socket.off('p2p:participant:controlled', handleParticipantControlled);
      socket.offAny();
    };
  }, [socket, handleRoomJoined, handleSocketError, handleUserJoined, handleOffer, handleAnswer, handleIceCandidate, handleParticipantLeft, handleControlReceived, handleParticipantControlled]);

  // Démarrer la réunion
  const startMeeting = async () => {
    if (!displayName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }

    if (!socket || !isConnected) {
      setError('Connexion au serveur en cours...');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const channel = channelName || generateChannelName();
      setChannelName(channel);

      // Obtenir le stream média (peut échouer si permission refusée, on continue quand même)
      // ensureMedia() retourne null si permission refusée, on continue quand même
      await ensureMedia();
      // Note: ensureMedia() gère maintenant les erreurs en interne et retourne null
      // si la permission est refusée, permettant de continuer sans média

      // Vérifier que le socket est bien connecté
      if (socket.connected) {
        console.log('[VideoCallPageP2P] ✅ Socket is connected');
      } else {
        console.warn('[VideoCallPageP2P] ⚠️ Socket is not connected, waiting...');
        // Attendre un peu que le socket se connecte
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!socket.connected) {
          throw new Error('Socket non connecté. Veuillez réessayer.');
        }
      }

      // Attendre que le socket soit vraiment connecté et que les listeners soient prêts
      if (!socket.connected) {
        console.warn('[VideoCallPageP2P] ⚠️ Socket not connected, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!socket.connected) {
          throw new Error('Socket non connecté. Veuillez réessayer.');
        }
      }

      // Vérifier que le listener est bien enregistré
      const hasListener = socket.hasListeners('p2p:room:joined');
      console.log('[VideoCallPageP2P] 📤 Has p2p:room:joined listener:', hasListener);
      
      if (!hasListener) {
        console.warn('[VideoCallPageP2P] ⚠️ Listener not registered yet, waiting 200ms...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Rejoindre la room via Socket.io
      console.log('[VideoCallPageP2P] 📤📤📤 Emitting p2p:room:join', { roomId: channel, userName: displayName });
      console.log('[VideoCallPageP2P] 📤 Socket ID before emit:', socket.id);
      console.log('[VideoCallPageP2P] 📤 Socket connected before emit:', socket.connected);
      
      socket.emit('p2p:room:join', {
        roomId: channel,
        userName: displayName
      });
      
      console.log('[VideoCallPageP2P] 📤✅ p2p:room:join emitted, waiting for response...');

      // Fallback : Si l'événement p2p:room:joined n'arrive pas dans 2 secondes,
      // forcer la redirection quand même (le stream est déjà obtenu)
      // Cela garantit que l'utilisateur voit au moins sa propre vidéo
      fallbackTimeoutRef.current = setTimeout(() => {
        console.log('[VideoCallPageP2P] ⏱️ Fallback: Forcing meeting start after 2s (stream already obtained)');
        setInMeeting((current) => {
          if (!current) {
            console.log('[VideoCallPageP2P] 🔄 Fallback: Setting inMeeting to true');
            setLoading(false);
            return true;
          }
          return current;
        });
      }, 2000); // 2 secondes

      // Nettoyer le fallback si l'événement arrive avant
      const cleanupFallback = () => {
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
      };

      // Écouter une fois l'événement pour nettoyer le fallback
      const onceHandler = ({ members, userName }) => {
        console.log('[VideoCallPageP2P] ✅ Received p2p:room:joined, cleaning up fallback');
        cleanupFallback();
        socket.off('p2p:room:joined', onceHandler);
      };
      socket.once('p2p:room:joined', onceHandler);

      // Timeout pour éviter que le chargement reste indéfini (10 secondes max)
      timeoutRef.current = setTimeout(() => {
        cleanupFallback();
        console.warn('[VideoCallPageP2P] ⚠️ Timeout: p2p:room:joined not received after 10s');
        setLoading((currentLoading) => {
          if (currentLoading) {
            setError('Timeout: La connexion à la salle a pris trop de temps. Veuillez réessayer.');
            return false;
          }
          return currentLoading;
        });
      }, 10000); // 10 secondes

      // inMeeting sera mis à true dans le handler 'p2p:room:joined' ou par le fallback
    } catch (err) {
      console.error('[VideoCallPageP2P] Error starting meeting:', err);
      setError(err.message || 'Impossible de démarrer la réunion');
      setLoading(false);
    }
  };


  // Résolutions disponibles pour l'enregistrement
  const recordingResolutions = {
    '480p': { width: 854, height: 480, label: '480p (SD)', bitrate: 1000000 },
    '720p': { width: 1280, height: 720, label: '720p (HD)', bitrate: 2000000 },
    '1080p': { width: 1920, height: 1080, label: '1080p (Full HD)', bitrate: 2500000 },
    '1440p': { width: 2560, height: 1440, label: '1440p (2K)', bitrate: 4000000 },
    '2160p': { width: 3840, height: 2160, label: '2160p (4K)', bitrate: 8000000 }
  };

  // Fonction pour formater le temps d'enregistrement
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Démarrer l'enregistrement
  const startRecording = useCallback(async () => {
    if (!inMeeting) {
      setError('Vous devez être dans une réunion pour enregistrer');
      return;
    }

    try {
      console.log('[VideoCallPageP2P] 🎬 Starting recording...');
      
      // Obtenir la résolution sélectionnée
      const resolution = recordingResolutions[recordingResolution] || recordingResolutions['1080p'];
      console.log('[VideoCallPageP2P] 📐 Recording resolution:', resolution.label, `${resolution.width}x${resolution.height}`);
      
      // Créer un canvas pour combiner les streams avec la résolution sélectionnée
      const canvas = document.createElement('canvas');
      canvas.width = resolution.width;
      canvas.height = resolution.height;
      const ctx = canvas.getContext('2d');
      canvasRef.current = canvas;

      // Vérifier que les vidéos sont prêtes
      const checkVideosReady = () => {
        let hasVideo = false;
        
        if (isScreenSharing && screenShareRef.current) {
          const video = screenShareRef.current;
          if (video && video.readyState >= 2 && video.videoWidth > 0) {
            hasVideo = true;
            console.log('[VideoCallPageP2P] ✅ Screen share video ready:', video.videoWidth, 'x', video.videoHeight);
          }
        } else if (localVideoRef.current && isVideoEnabled) {
          const video = localVideoRef.current;
          if (video && video.readyState >= 2 && video.videoWidth > 0) {
            hasVideo = true;
            console.log('[VideoCallPageP2P] ✅ Local video ready:', video.videoWidth, 'x', video.videoHeight);
          }
        }
        
        remoteStreams.forEach(({ id }) => {
          const videoElement = remoteVideosRef.current[id];
          if (videoElement && videoElement.readyState >= 2 && videoElement.videoWidth > 0) {
            hasVideo = true;
            console.log('[VideoCallPageP2P] ✅ Remote video ready:', id, videoElement.videoWidth, 'x', videoElement.videoHeight);
          }
        });
        
        if (!hasVideo) {
          console.warn('[VideoCallPageP2P] ⚠️ No video streams ready for recording');
        }
        
        return hasVideo;
      };

      // Créer un stream composite depuis le canvas
      const canvasStream = canvas.captureStream(30); // 30 FPS
      
      // Ajouter les tracks audio du stream local
      if (myStream) {
        const audioTracks = myStream.getAudioTracks();
        audioTracks.forEach(track => {
          canvasStream.addTrack(track);
        });
      }

      // Ajouter les tracks audio des streams distants
      remoteStreams.forEach(({ stream }) => {
        const audioTracks = stream.getAudioTracks();
        audioTracks.forEach(track => {
          canvasStream.addTrack(track);
        });
      });

      // Fonction pour mettre à jour le canvas avec les vidéos
      const updateCanvas = () => {
        if (!isRecordingRef.current) {
          console.log('[VideoCallPageP2P] ⚠️ updateCanvas called but recording is false');
          return;
        }

        try {
          // Effacer le canvas
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Dessiner le stream local (ou écran partagé) en grand
          if (isScreenSharing && screenShareRef.current) {
            const video = screenShareRef.current;
            if (video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
              const aspectRatio = video.videoWidth / video.videoHeight;
              const drawWidth = canvas.width;
              const drawHeight = drawWidth / aspectRatio;
              const y = (canvas.height - drawHeight) / 2;
              ctx.drawImage(video, 0, y, drawWidth, drawHeight);
            }
          } else if (localVideoRef.current && isVideoEnabled) {
            const video = localVideoRef.current;
            if (video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
              const aspectRatio = video.videoWidth / video.videoHeight;
              const drawWidth = canvas.width / 2;
              const drawHeight = drawWidth / aspectRatio;
              const y = (canvas.height - drawHeight) / 2;
              ctx.drawImage(video, 0, y, drawWidth, drawHeight);
            }
          }

          // Dessiner les streams distants
          let xOffset = canvas.width / 2;
          const remoteWidth = canvas.width / 2;
          const remoteHeight = canvas.height / Math.max(1, remoteStreams.length);

          remoteStreams.forEach(({ id }, index) => {
            const videoElement = remoteVideosRef.current[id];
            if (videoElement && videoElement.readyState >= 2 && videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
              const aspectRatio = videoElement.videoWidth / videoElement.videoHeight;
              const drawWidth = Math.min(remoteWidth, remoteHeight * aspectRatio);
              const drawHeight = drawWidth / aspectRatio;
              const y = index * remoteHeight + (remoteHeight - drawHeight) / 2;
              ctx.drawImage(videoElement, xOffset, y, drawWidth, drawHeight);
            }
          });
        } catch (error) {
          console.error('[VideoCallPageP2P] ❌ Error in updateCanvas:', error);
        }

        // Continuer la boucle d'animation seulement si on enregistre encore
        if (isRecordingRef.current) {
          requestAnimationFrame(updateCanvas);
        }
      };

      // Attendre un peu que les vidéos soient prêtes avant de démarrer
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Vérifier que les vidéos sont prêtes
      const videosReady = checkVideosReady();
      if (!videosReady) {
        console.warn('[VideoCallPageP2P] ⚠️ Some videos may not be ready, but continuing...');
      }

      // Mettre isRecordingRef à true AVANT de démarrer updateCanvas
      isRecordingRef.current = true;
      
      // Démarrer la mise à jour du canvas
      updateCanvas();
      
      // Attendre encore un peu pour que le canvas commence à être mis à jour
      await new Promise(resolve => setTimeout(resolve, 200));

      // Créer le MediaRecorder avec la résolution et le bitrate appropriés
      const options = {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: resolution.bitrate
      };

      // Fallback si vp9 n'est pas supporté
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8,opus';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm';
        }
      }

      const mediaRecorder = new MediaRecorder(canvasStream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
          console.log('[VideoCallPageP2P] 📦 Recording chunk received, size:', event.data.size, 'bytes');
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ Empty chunk received');
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('[VideoCallPageP2P] 🛑 Recording stopped, total chunks:', recordedChunksRef.current.length);
        
        const totalSize = recordedChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
        console.log('[VideoCallPageP2P] 📊 Total recording size:', totalSize, 'bytes');
        
        if (totalSize === 0) {
          console.error('[VideoCallPageP2P] ❌ No data recorded! Recording failed.');
          setError('Erreur: Aucune donnée enregistrée. Vérifiez que la vidéo est active.');
          return;
        }
        
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('[VideoCallPageP2P] 📦 Blob created, size:', blob.size, 'bytes');
        
        const resolutionLabel = recordingResolutions[recordingResolution]?.label || '1080p';
        const fileName = `meeting-recording-${channelName}-${resolutionLabel}-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
        
        // Téléchargement local (fallback)
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Enregistrer dans Stockage sécurisé (VaultSphere - dossier Teams video)
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const participantIds = [user._id];
          (participantsRef.current || []).forEach((p) => {
            if (p.userId && !participantIds.includes(p.userId)) participantIds.push(p.userId);
          });
          
          const formData = new FormData();
          formData.append('file', blob, fileName);
          formData.append('participantIds', JSON.stringify(participantIds));
          if (meetingEventIdRef.current) formData.append('meetingEventId', meetingEventIdRef.current);
          
          const res = await vaultSphereAPI.uploadMeetingRecording(formData);
          if (res.data?.success) {
            setError('');
            const count = res.data.sharedWithCount || 0;
            setTimeout(() => {
              setError(`✅ Enregistrement sauvegardé dans Stockage sécurisé → dossier "Teams video"${count > 0 ? ` (partagé avec ${count} participant${count > 1 ? 's' : ''})` : ''}`);
              setTimeout(() => setError(''), 6000);
            }, 100);
          }
        } catch (err) {
          console.warn('[VideoCallPageP2P] ⚠️ Upload VaultSphere failed:', err);
          setError('Enregistrement téléchargé. Échec sauvegarde Stockage sécurisé: ' + (err.response?.data?.error || err.message));
          setTimeout(() => setError(''), 6000);
        }

        recordedChunksRef.current = [];
        if (canvasRef.current) canvasRef.current = null;
        console.log('[VideoCallPageP2P] ✅ Recording completed');
      };

      mediaRecorder.onerror = (event) => {
        console.error('[VideoCallPageP2P] ❌ Recording error:', event.error);
        setError('Erreur lors de l\'enregistrement: ' + event.error.message);
        setIsRecording(false);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        setRecordingTime(0);
      };

      // Démarrer l'enregistrement (isRecordingRef est déjà à true)
      mediaRecorder.start(1000); // Collecter les données toutes les secondes
      setIsRecording(true);
      setRecordingTime(0);
      
      console.log('[VideoCallPageP2P] ✅ Recording started, MediaRecorder state:', mediaRecorder.state);

      // Démarrer le timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log('[VideoCallPageP2P] ✅ Recording started');
    } catch (error) {
      console.error('[VideoCallPageP2P] ❌ Error starting recording:', error);
      setError('Erreur lors du démarrage de l\'enregistrement: ' + error.message);
      setIsRecording(false);
    }
  }, [inMeeting, myStream, remoteStreams, isScreenSharing, isVideoEnabled, channelName, recordingResolution]);

  // Arrêter l'enregistrement
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('[VideoCallPageP2P] 🛑 Stopping recording...');
      isRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Arrêter le timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [isRecording]);

  // Ouvrir le dialogue de sélection de résolution
  const openRecordingDialog = () => {
    if (!inMeeting) {
      setError('Vous devez être dans une réunion pour enregistrer');
      return;
    }
    setShowResolutionDialog(true);
  };

  // Confirmer et démarrer l'enregistrement avec la résolution choisie
  const confirmAndStartRecording = useCallback(async () => {
    setShowResolutionDialog(false);
    // Attendre un peu pour que le dialogue se ferme
    await new Promise(resolve => setTimeout(resolve, 100));
    // Démarrer l'enregistrement
    await startRecording();
  }, [startRecording]);

  // Nettoyer l'enregistrement au démontage
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (canvasRef.current) {
        canvasRef.current = null;
      }
    };
  }, [isRecording]);

  // Toggle audio - Amélioré pour une meilleure synchronisation
  const toggleAudio = () => {
    if (!myStream) {
      console.warn('[VideoCallPageP2P] ⚠️ No local stream available, cannot toggle audio');
      return;
    }

    // Si l'utilisateur réactive son micro après qu'il ait été désactivé par l'organisateur
    if (forcedMuted) {
      console.log('[VideoCallPageP2P] ℹ️ User reactivating audio after organizer mute');
      setForcedMuted(false); // Réinitialiser le flag pour permettre l'activation
    }

    // Toggle les tracks audio du stream local
    const audioTracks = myStream.getAudioTracks();
    let newState = true;
    
    if (audioTracks.length > 0) {
      // Vérifier l'état actuel de tous les tracks pour une meilleure synchronisation
      const allTracksEnabled = audioTracks.every(track => track.enabled && track.readyState === 'live');
      const allTracksDisabled = audioTracks.every(track => !track.enabled || track.readyState !== 'live');
      
      // Déterminer le nouvel état basé sur l'état actuel
      const currentState = allTracksEnabled && !allTracksDisabled;
      
      // Appliquer le changement à tous les tracks
      audioTracks.forEach(track => {
        if (track.readyState === 'live') {
          track.enabled = !currentState;
          console.log(`[VideoCallPageP2P] 🔊 Audio track ${track.id} set to:`, track.enabled);
        } else if (track.readyState === 'ended') {
          // Le track est terminé, on ne peut pas le modifier
          console.warn(`[VideoCallPageP2P] ⚠️ Audio track ${track.id} is ended, cannot toggle. Need to get new stream.`);
          // Si tous les tracks sont terminés, on doit obtenir un nouveau stream
          if (audioTracks.every(t => t.readyState === 'ended')) {
            console.log('[VideoCallPageP2P] 🔄 All audio tracks ended, need to get new media stream');
            // Ne pas essayer de récupérer automatiquement ici, laisser l'utilisateur le faire
            setError('⚠️ Votre micro a été désactivé. Veuillez réactiver votre micro.');
            setTimeout(() => setError(''), 5000);
            return;
          }
        } else {
          console.log(`[VideoCallPageP2P] ℹ️ Audio track ${track.id} state: ${track.readyState}`);
        }
      });
      
      newState = !currentState;
      console.log('[VideoCallPageP2P] 🔊 Audio toggled:', newState ? 'ON' : 'OFF');
      
      // Vérifier que le changement a bien été appliqué
      setTimeout(() => {
        const actualState = audioTracks.some(track => track.enabled && track.readyState === 'live');
        if (actualState !== newState) {
          console.warn('[VideoCallPageP2P] ⚠️ Audio state mismatch, correcting...');
          audioTracks.forEach(track => {
            if (track.readyState === 'live') {
              track.enabled = newState;
            }
          });
        }
      }, 100);
    } else {
      console.warn('[VideoCallPageP2P] ⚠️ No audio tracks found in stream');
      newState = false;
    }

    // Toggle les tracks audio dans toutes les connexions peer
    const peerState = peerManager.toggleAudio();
    if (peerState !== null) {
      newState = peerState;
    }

    setIsAudioEnabled(newState);
  };

  // Toggle video
  const toggleVideo = async () => {
    if (!myStream) {
      console.warn('[VideoCallPageP2P] ⚠️ No local stream available, cannot toggle video');
      return;
    }

    // Si l'utilisateur réactive sa caméra après qu'elle ait été désactivée par l'organisateur
    if (forcedVideoOff) {
      console.log('[VideoCallPageP2P] ℹ️ User reactivating video after organizer disable');
      setForcedVideoOff(false); // Réinitialiser le flag pour permettre l'activation
    }

    const videoTracks = myStream.getVideoTracks();
    let newState = true;
    
    if (videoTracks.length > 0) {
      const currentState = videoTracks[0].enabled && videoTracks[0].readyState === 'live';
      newState = !currentState;
      
      // Mettre à jour l'état UI immédiatement pour éviter les problèmes de synchronisation
      setIsVideoEnabled(newState);
      
      if (newState) {
        // Réactiver la vidéo
        if (videoTracks[0].readyState === 'ended') {
          // Les tracks sont arrêtés, obtenir un nouveau stream vidéo
          console.log('[VideoCallPageP2P] 📹 Video tracks ended, getting new video stream');
          try {
            // S'assurer que tous les anciens tracks vidéo sont complètement arrêtés
            const oldVideoTracks = myStream.getVideoTracks();
            oldVideoTracks.forEach(track => {
              if (track.readyState !== 'ended') {
                track.stop();
              }
            });
            
            // Attendre un peu pour que la caméra soit libérée
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Obtenir un nouveau stream avec vidéo + audio existant
            const audioTracks = myStream.getAudioTracks();
            const hasAudio = audioTracks.length > 0 && audioTracks[0].readyState === 'live';
            
            const newStream = await navigator.mediaDevices.getUserMedia({
              audio: false, // Ne pas demander l'audio, on garde celui existant
              video: { width: 1280, height: 720 }
            });
            
            // Créer un nouveau MediaStream avec les tracks audio existants + nouveaux tracks vidéo
            const combinedStream = new MediaStream();
            
            // Ajouter les tracks audio existants (si disponibles)
            if (hasAudio) {
              audioTracks.forEach(track => {
                if (track.readyState === 'live') {
                  combinedStream.addTrack(track);
                }
              });
            }
            
            // Ajouter les nouveaux tracks vidéo
            newStream.getVideoTracks().forEach(track => {
              combinedStream.addTrack(track);
            });
            
            // Arrêter les tracks audio du nouveau stream (on n'en a pas besoin)
            newStream.getAudioTracks().forEach(track => track.stop());
            
            // Arrêter complètement l'ancien stream
            myStream.getTracks().forEach(track => {
              if (track.readyState !== 'ended') {
                track.stop();
              }
            });
            
            // Mettre à jour le stream
            setMyStream(combinedStream);
            
            // Mettre à jour l'affichage immédiatement
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = combinedStream;
              localVideoRef.current.style.display = 'block';
              localVideoRef.current.play().catch(err => {
                if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                  console.warn('[VideoCallPageP2P] Error playing local video:', err.name);
                }
              });
            }
            
            // Mettre à jour les connexions peer avec les nouveaux tracks et créer une nouvelle offre
            // Attendre un peu pour que le stream soit bien mis à jour
            setTimeout(async () => {
              for (const remoteId of peersRef.current) {
                if (peerManager.hasPeer(remoteId)) {
                  const peer = peerManager.peers?.get(remoteId);
                  if (peer) {
                    console.log('[VideoCallPageP2P] 📹 Adding new video tracks to peer:', remoteId, 'State:', peer.signalingState);
                    
                    // Remplacer les anciens tracks vidéo par les nouveaux
                    const existingSenders = peer.getSenders();
                    const videoSenders = existingSenders.filter(s => s.track?.kind === 'video');
                    
                    // Remplacer les senders vidéo existants
                    for (const sender of videoSenders) {
                      if (sender.track) {
                        try {
                          await sender.replaceTrack(null); // Retirer l'ancien track
                        } catch (err) {
                          console.warn('[VideoCallPageP2P] Error replacing track:', err);
                        }
                      }
                    }
                    
                    // Ajouter les nouveaux tracks vidéo
                    combinedStream.getVideoTracks().forEach(track => {
                      try {
                        peer.addTrack(track, combinedStream);
                        console.log('[VideoCallPageP2P] ✅ Added video track to peer:', remoteId, track.id);
                      } catch (err) {
                        console.warn('[VideoCallPageP2P] Error adding track to peer:', err);
                      }
                    });
                    
                    // Si la connexion est stable, créer une nouvelle offre pour inclure les nouveaux tracks
                    if (peer.signalingState === 'stable') {
                      try {
                        const offer = await peerManager.createOffer(remoteId);
                        if (offer && socket) {
                          console.log('[VideoCallPageP2P] 📤 Sending updated offer after video reactivation to:', remoteId);
                          socket.emit('p2p:offer', {
                            to: remoteId,
                            offer: offer,
                            roomId: channelName,
                            userName: displayName
                          });
                        } else {
                          console.warn('[VideoCallPageP2P] ⚠️ Could not create offer, state:', peer.signalingState);
                        }
                      } catch (err) {
                        console.warn('[VideoCallPageP2P] Error creating offer after video reactivation:', err);
                      }
                    } else {
                      console.log('[VideoCallPageP2P] ⚠️ Connection not stable, cannot create offer. State:', peer.signalingState);
                    }
                  }
                }
              }
            }, 500); // Augmenter le délai pour laisser le temps à la caméra de se stabiliser
            
            console.log('[VideoCallPageP2P] ✅ Video reactivated');
          } catch (error) {
            console.error('[VideoCallPageP2P] ❌ Error reactivating video:', error);
            let errorMessage = 'Impossible de réactiver la caméra';
            if (error.name === 'NotReadableError') {
              errorMessage = 'La caméra est déjà utilisée. Veuillez fermer les autres applications qui utilisent la caméra.';
            }
            setError(errorMessage);
            setTimeout(() => setError(''), 5000);
            // Remettre l'état à false en cas d'erreur
            setIsVideoEnabled(false);
            return;
          }
        } else {
          // Les tracks existent encore, juste les réactiver
          console.log('[VideoCallPageP2P] 📹 Activating video');
          videoTracks.forEach(track => {
            track.enabled = true;
          });
          
          // Mettre à jour l'affichage
          if (localVideoRef.current) {
            localVideoRef.current.style.display = 'block';
          }
          
          // Toggle les tracks vidéo dans toutes les connexions peer
          peerManager.toggleVideo();
        }
      } else {
        // Désactiver la vidéo : arrêter complètement les tracks pour éteindre la caméra
        console.log('[VideoCallPageP2P] 📹 Deactivating video (stopping tracks to turn off camera)');
        videoTracks.forEach(track => {
          track.enabled = false;
          track.stop(); // Arrêter complètement le track pour éteindre la caméra
        });
        
        // Mettre à jour l'affichage
        if (localVideoRef.current) {
          localVideoRef.current.style.display = 'none';
        }
        
        // Toggle les tracks vidéo dans toutes les connexions peer
        peerManager.toggleVideo();
      }
    } else {
      // Pas de tracks vidéo, l'état est false
      newState = false;
      setIsVideoEnabled(false);
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    try {
      if (isScreenSharing && screenStream) {
        // Arrêter le partage d'écran
        console.log('[VideoCallPageP2P] 🖥️ Stopping screen share');
        
        // Arrêter tous les tracks d'écran
        screenStream.getTracks().forEach(track => {
          track.stop();
        });
        
        // Remplacer les tracks d'écran par les tracks vidéo originaux dans toutes les connexions peer
        if (myStream && originalVideoTracksRef.current.length > 0) {
          for (const remoteId of peersRef.current) {
            if (peerManager.hasPeer(remoteId)) {
              const peer = peerManager.peers?.get(remoteId);
              if (peer) {
                const existingSenders = peer.getSenders();
                const screenSenders = existingSenders.filter(s => s.track?.kind === 'video' && s.track.label.includes('screen'));
                
                // Remplacer les senders d'écran par les tracks vidéo originaux
                for (let i = 0; i < screenSenders.length && i < originalVideoTracksRef.current.length; i++) {
                  try {
                    await screenSenders[i].replaceTrack(originalVideoTracksRef.current[i]);
                    console.log('[VideoCallPageP2P] ✅ Replaced screen track with original video track');
                  } catch (err) {
                    console.warn('[VideoCallPageP2P] Error replacing screen track:', err);
                    // Si le remplacement échoue, ajouter le track original
                    peer.addTrack(originalVideoTracksRef.current[i], myStream);
                  }
                }
                
                // Si la connexion est stable, créer une nouvelle offre
                if (peer.signalingState === 'stable') {
                  setTimeout(async () => {
                    try {
                      const offer = await peerManager.createOffer(remoteId);
                      if (offer && socket) {
                        socket.emit('p2p:offer', {
                          to: remoteId,
                          offer: offer,
                          roomId: channelName,
                          userName: displayName
                        });
                      }
                    } catch (err) {
                      console.warn('[VideoCallPageP2P] Error creating offer after stopping screen share:', err);
                    }
                  }, 200);
                }
              }
            }
          }
        }
        
        setScreenStream(null);
        setIsScreenSharing(false);
        originalVideoTracksRef.current = [];
        
        // Cacher l'élément d'écran partagé
        if (screenShareRef.current) {
          screenShareRef.current.style.display = 'none';
        }
        
        // Réafficher la vidéo locale si elle était activée
        if (localVideoRef.current && isVideoEnabled) {
          localVideoRef.current.style.display = 'block';
        }
        
        console.log('[VideoCallPageP2P] ✅ Screen share stopped');
      } else {
        // Démarrer le partage d'écran
        console.log('[VideoCallPageP2P] 🖥️ Starting screen share');
        
        // Demander l'accès à l'écran
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            displaySurface: 'monitor'
          },
          audio: false
        });
        
        // Sauvegarder les tracks vidéo originaux
        if (myStream) {
          originalVideoTracksRef.current = myStream.getVideoTracks();
        }
        
        // Créer un nouveau stream combiné avec l'audio existant + l'écran
        const combinedStream = new MediaStream();
        
        // Ajouter les tracks audio existants (si disponibles)
        if (myStream) {
          myStream.getAudioTracks().forEach(track => {
            if (track.readyState === 'live') {
              combinedStream.addTrack(track);
            }
          });
        }
        
        // Ajouter les tracks d'écran
        displayStream.getVideoTracks().forEach(track => {
          combinedStream.addTrack(track);
          
          // Gérer l'arrêt automatique si l'utilisateur arrête le partage depuis le navigateur
          track.onended = () => {
            console.log('[VideoCallPageP2P] 🖥️ Screen share ended by user');
            toggleScreenShare();
          };
        });
        
        setScreenStream(combinedStream);
        setIsScreenSharing(true);
        
        // Afficher l'écran partagé localement
        if (screenShareRef.current) {
          screenShareRef.current.srcObject = combinedStream;
          screenShareRef.current.style.display = 'block';
          screenShareRef.current.play().catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              console.warn('[VideoCallPageP2P] Error playing screen share:', err.name);
            }
          });
        }
        
        // Cacher temporairement la vidéo locale
        if (localVideoRef.current) {
          localVideoRef.current.style.display = 'none';
        }
        
        // Remplacer les tracks vidéo par les tracks d'écran dans toutes les connexions peer
        setTimeout(async () => {
          for (const remoteId of peersRef.current) {
            if (peerManager.hasPeer(remoteId)) {
              const peer = peerManager.peers?.get(remoteId);
              if (peer) {
                const existingSenders = peer.getSenders();
                const videoSenders = existingSenders.filter(s => s.track?.kind === 'video');
                
                // Remplacer les tracks vidéo par les tracks d'écran
                const screenTracks = combinedStream.getVideoTracks();
                for (let i = 0; i < videoSenders.length && i < screenTracks.length; i++) {
                  try {
                    await videoSenders[i].replaceTrack(screenTracks[i]);
                    console.log('[VideoCallPageP2P] ✅ Replaced video track with screen track');
                  } catch (err) {
                    console.warn('[VideoCallPageP2P] Error replacing video track with screen:', err);
                    peer.addTrack(screenTracks[i], combinedStream);
                  }
                }
                
                // Si la connexion est stable, créer une nouvelle offre
                if (peer.signalingState === 'stable') {
                  try {
                    const offer = await peerManager.createOffer(remoteId);
                    if (offer && socket) {
                      console.log('[VideoCallPageP2P] 📤 Sending offer with screen share to:', remoteId);
                      socket.emit('p2p:offer', {
                        to: remoteId,
                        offer: offer,
                        roomId: channelName,
                        userName: displayName
                      });
                    }
                  } catch (err) {
                    console.warn('[VideoCallPageP2P] Error creating offer with screen share:', err);
                  }
                }
              }
            }
          }
        }, 300);
        
        console.log('[VideoCallPageP2P] ✅ Screen share started');
      }
    } catch (error) {
      console.error('[VideoCallPageP2P] ❌ Error toggling screen share:', error);
      let errorMessage = 'Erreur lors du partage d\'écran';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Partage d\'écran refusé. Veuillez autoriser l\'accès à l\'écran.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Aucun écran disponible pour le partage.';
      }
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
      setIsScreenSharing(false);
    }
  };

  // Fonction pour agrandir/réduire une vidéo
  const toggleExpandVideo = (videoId, videoType = 'camera') => {
    if (expandedVideo && expandedVideo.id === videoId && expandedVideo.type === videoType) {
      // Réduire si c'est déjà agrandi
      setExpandedVideo(null);
    } else {
      // Agrandir
      setExpandedVideo({ id: videoId, type: videoType });
    }
  };

  // Obtenir le stream à afficher pour une vidéo agrandie
  const getExpandedVideoStream = () => {
    if (!expandedVideo) return null;
    
    if (expandedVideo.id === 'local') {
      // Vidéo locale : caméra ou écran partagé
      if (expandedVideo.type === 'screen' && screenStream) {
        return { stream: screenStream, userName: displayName || 'Vous', isLocal: true };
      } else if (expandedVideo.type === 'camera' && myStream) {
        return { stream: myStream, userName: displayName || 'Vous', isLocal: true };
      }
    } else {
      // Vidéo distante
      const remoteStream = remoteStreams.find(rs => rs.id === expandedVideo.id);
      if (remoteStream) {
        return { stream: remoteStream.stream, userName: remoteStream.userName, isLocal: false };
      }
    }
    return null;
  };

  // Copier le lien de la réunion
  const copyMeetingLink = () => {
    // S'assurer que channelName est défini
    if (!channelName || channelName.trim() === '') {
      console.warn('[VideoCallPageP2P] ⚠️ channelName is empty, cannot copy link');
      setError('Impossible de copier le lien : ID de la salle non défini. Attendez que la réunion démarre.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    const link = `${window.location.origin}/video-call?room=${channelName}&mode=p2p`;
    console.log('[VideoCallPageP2P] 📋 Copying meeting link:', link);
    console.log('[VideoCallPageP2P] 📋 Current channelName:', channelName);
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.error('[VideoCallPageP2P] Error copying link:', err);
      setError('Erreur lors de la copie du lien');
      setTimeout(() => setError(''), 3000);
    });
  };

  // Synchroniser l'état UI avec l'état réel des tracks
  useEffect(() => {
    if (myStream) {
      const videoTracks = myStream.getVideoTracks();
      const audioTracks = myStream.getAudioTracks();
      
      // Synchroniser l'état vidéo
      if (videoTracks.length > 0) {
        const videoEnabled = videoTracks[0].enabled && videoTracks[0].readyState === 'live';
        if (isVideoEnabled !== videoEnabled) {
          console.log('[VideoCallPageP2P] 🔄 Syncing video state:', videoEnabled);
          setIsVideoEnabled(videoEnabled);
        }
      } else {
        if (isVideoEnabled !== false) {
          setIsVideoEnabled(false);
        }
      }
      
      // Synchroniser l'état audio
      if (audioTracks.length > 0) {
        // Filtrer seulement les tracks qui sont live
        const liveAudioTracks = audioTracks.filter(track => track.readyState === 'live');
        if (liveAudioTracks.length > 0) {
          const audioEnabled = liveAudioTracks[0].enabled;
          if (isAudioEnabled !== audioEnabled) {
            console.log('[VideoCallPageP2P] 🔄 Syncing audio state:', audioEnabled);
            setIsAudioEnabled(audioEnabled);
          }
        } else {
          // Tous les tracks audio sont terminés
          if (isAudioEnabled !== false) {
            console.warn('[VideoCallPageP2P] ⚠️ All audio tracks ended, setting audio to disabled');
            setIsAudioEnabled(false);
          }
        }
      } else {
        if (isAudioEnabled !== false) {
          setIsAudioEnabled(false);
        }
      }
    }
  }, [myStream]); // Ne pas inclure isVideoEnabled/isAudioEnabled dans les dépendances pour éviter les boucles

  // Mettre à jour la vidéo locale quand le stream change avec retry amélioré
  useEffect(() => {
    if (!myStream) return;

    const attachStreamToLocalVideo = async (attempt = 0) => {
      const MAX_ATTEMPTS = 10;
      const RETRY_DELAY = 200;

      // Vérifier que l'élément vidéo existe et est monté
      if (!localVideoRef.current) {
        if (attempt < MAX_ATTEMPTS) {
          console.log(`[VideoCallPageP2P] ⏳ Waiting for localVideoRef (attempt ${attempt + 1}/${MAX_ATTEMPTS})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return attachStreamToLocalVideo(attempt + 1);
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ localVideoRef not available after all retries');
          return;
        }
      }

      const videoElement = localVideoRef.current;
      
      // Vérifier que l'élément est dans le DOM et visible
      if (!videoElement.isConnected) {
        if (attempt < MAX_ATTEMPTS) {
          console.log(`[VideoCallPageP2P] ⏳ Video element not connected to DOM (attempt ${attempt + 1}/${MAX_ATTEMPTS})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return attachStreamToLocalVideo(attempt + 1);
        } else {
          console.warn('[VideoCallPageP2P] ⚠️ Video element not connected after all retries');
          return;
        }
      }

      // Ne mettre à jour que si le stream a changé
      if (videoElement.srcObject !== myStream) {
        try {
          console.log('[VideoCallPageP2P] 📹 Attaching stream to local video element');
          console.log('[VideoCallPageP2P] 📹 Stream tracks:', myStream.getTracks().map(t => ({ 
            kind: t.kind, 
            enabled: t.enabled, 
            readyState: t.readyState 
          })));
          
          videoElement.srcObject = myStream;
          
          // Attendre un peu pour que l'élément soit prêt
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Forcer la lecture avec gestion d'erreur améliorée
          try {
            await videoElement.play();
            console.log('[VideoCallPageP2P] ✅ Local video playing successfully');
          } catch (playErr) {
            // Ignorer les erreurs AbortError (interruption normale lors du changement de stream)
            if (playErr.name !== 'AbortError' && playErr.name !== 'NotAllowedError') {
              console.warn('[VideoCallPageP2P] ⚠️ Error playing local video:', playErr.name, playErr.message);
              // Retry une fois si ce n'est pas une erreur de permission
              if (attempt < 2 && playErr.name !== 'NotAllowedError') {
                await new Promise(resolve => setTimeout(resolve, 300));
                return attachStreamToLocalVideo(attempt + 1);
              }
            }
          }
        } catch (err) {
          console.error('[VideoCallPageP2P] ❌ Error attaching stream to local video:', err);
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 300));
            return attachStreamToLocalVideo(attempt + 1);
          }
        }
      } else {
        // Stream déjà attaché, s'assurer qu'il joue
        if (videoElement.paused) {
          videoElement.play().catch(err => {
            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              console.warn('[VideoCallPageP2P] ⚠️ Error playing already attached video:', err.name);
            }
          });
        }
      }
    };

    // Démarrer l'attachement
    attachStreamToLocalVideo().catch(err => {
      console.error('[VideoCallPageP2P] ❌ Failed to attach stream to local video:', err);
    });
  }, [myStream, isVideoEnabled]); // Ajouter isVideoEnabled pour réagir aux changements de visibilité

  // Quand le stream local devient disponible, mettre à jour toutes les connexions peer existantes
  useEffect(() => {
    if (myStream && inMeeting && peersRef.current.size > 0) {
      console.log('[VideoCallPageP2P] 📹 Local stream available, updating all existing peer connections');
      
      const updatePeerConnections = async () => {
        for (const remoteId of peersRef.current) {
          if (peerManager.hasPeer(remoteId)) {
            const peer = peerManager.peers?.get(remoteId);
            if (!peer) continue;
            
            console.log('[VideoCallPageP2P] 📹 Adding local tracks to existing peer:', remoteId, 'Signaling state:', peer.signalingState);
            
            // Vérifier quels tracks sont déjà présents
            const existingSenders = peer.getSenders();
            const existingVideoTracks = existingSenders.filter(s => s.track?.kind === 'video').map(s => s.track?.id).filter(Boolean);
            const existingAudioTracks = existingSenders.filter(s => s.track?.kind === 'audio').map(s => s.track?.id).filter(Boolean);
            
            const videoTracks = myStream.getVideoTracks();
            const audioTracks = myStream.getAudioTracks();
            
            let needsUpdate = false;
            
            // Ajouter les tracks vidéo manquants
            videoTracks.forEach(track => {
              if (!existingVideoTracks.includes(track.id)) {
                console.log('[VideoCallPageP2P] 📹 Adding video track to peer:', remoteId, track.id);
                peer.addTrack(track, myStream);
                needsUpdate = true;
              }
            });
            
            // Ajouter les tracks audio manquants
            audioTracks.forEach(track => {
              if (!existingAudioTracks.includes(track.id)) {
                console.log('[VideoCallPageP2P] 🔊 Adding audio track to peer:', remoteId, track.id);
                peer.addTrack(track, myStream);
                needsUpdate = true;
              }
            });
            
            // Si on a ajouté des tracks et que la connexion est stable, créer une nouvelle offre
            if (needsUpdate && peer.signalingState === 'stable') {
              console.log('[VideoCallPageP2P] 📤 Connection is stable with new tracks, creating new offer for:', remoteId);
              try {
                // Attendre un peu pour que les tracks soient bien intégrés
                await new Promise(resolve => setTimeout(resolve, 200));
                
                const offer = await peerManager.createOffer(remoteId);
                if (offer && socket) {
                  console.log('[VideoCallPageP2P] 📤 Sending updated offer to:', remoteId);
                  socket.emit('p2p:offer', {
                    to: remoteId,
                    offer: offer,
                    roomId: channelName,
                    userName: displayName
                  });
                }
              } catch (err) {
                console.warn('[VideoCallPageP2P] Error creating offer for existing peer:', err);
              }
            }
          }
        }
      };
      
      // Attendre un peu avant de mettre à jour pour s'assurer que les connexions sont prêtes
      setTimeout(() => {
        updatePeerConnections();
      }, 500);
    }
  }, [myStream, inMeeting, socket, channelName, displayName]);

  // Nettoyage à la déconnexion du composant uniquement
  useEffect(() => {
    return () => {
      console.log('[VideoCallPageP2P] 🧹 Component unmounting, cleaning up...');
      // Nettoyer les timeouts si présents
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      
      // Nettoyer les connexions peer
      peerManager.closeAll();
      peersRef.current.clear();
      
      // Arrêter le stream local
      if (myStream) {
        myStream.getTracks().forEach((track) => track.stop());
      }
      
      // Quitter la room seulement si on est vraiment en train de démonter le composant
      // Utiliser une ref pour éviter les dépendances qui changent
      const currentChannelName = channelName;
      if (socket && currentChannelName && socket.connected) {
        console.log('[VideoCallPageP2P] 🚪 Leaving room on unmount:', currentChannelName);
        socket.emit('p2p:room:leave', { roomId: currentChannelName });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Pas de dépendances - seulement au démontage du composant

  // Afficher le formulaire de démarrage
  if (!inMeeting) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold mb-6 text-center">Réunion P2P</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {channelName && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">📥 Réunion trouvée</p>
                <p className="text-xs text-blue-600 mt-1">ID de la salle: <code className="bg-blue-100 px-1 rounded">{channelName}</code></p>
                <p className="text-xs text-blue-600 mt-1">Entrez votre nom et cliquez sur "Démarrer la réunion" pour rejoindre</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-2">Votre nom</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Entrez votre nom"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">ID de la salle (optionnel)</label>
              <input
                type="text"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Laissez vide pour créer une nouvelle salle"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pour rejoindre une réunion existante, entrez l'ID de la salle ou utilisez le lien d'invitation
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              {isConnected ? 'Connecté au serveur' : 'Déconnecté'}
            </div>

            <button
              onClick={startMeeting}
              disabled={loading || !isConnected}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Connexion à la salle...' : 'Démarrer la réunion'}
            </button>
            
            {loading && (
              <div className="text-center text-sm text-gray-500 mt-2">
                Ouverture de la caméra et connexion à la salle...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'interface de réunion
  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* En-tête */}
      <div className="bg-gray-800 p-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Salle: {channelName}</h2>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            {participants.length + 1} participant(s) • Mode P2P
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={copyMeetingLink}
            disabled={!channelName || channelName.trim() === ''}
            className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-sm font-medium"
            title={channelName ? "Copier le lien d'invitation pour partager avec d'autres participants" : "Attendez que la réunion démarre pour copier le lien"}
          >
            {copied ? '✓ Lien copié' : '📋 Copier le lien'}
          </button>
          <div className="px-4 py-2 bg-gray-700 rounded text-sm flex items-center gap-2">
            <Users className="w-4 h-4" />
            {participants.length + 1} participant{participants.length > 0 ? 's' : ''}
          </div>
          {isOrganizer && (
            <div className="px-4 py-2 bg-purple-600 rounded text-sm flex items-center gap-2">
              <Crown className="w-4 h-4" />
              Organisateur
            </div>
          )}
        </div>
      </div>

      {insecureContextWarning && (
        <div className="p-4 text-center bg-amber-600 text-white border-b-2 border-amber-700">
          <p className="font-semibold">🔒 Connexion non sécurisée (HTTP)</p>
          <p className="text-sm mt-1 opacity-95">La caméra et le micro nécessitent HTTPS. Utilisez <strong>https://</strong> ou <strong>localhost</strong> pour activer la vidéo et l&apos;audio.</p>
          <p className="text-xs mt-2 opacity-80">Avec Tailscale : configurez HTTPS (Funnel, Caddy, ou nginx avec Let&apos;s Encrypt).</p>
        </div>
      )}
      {error && (
        <div className={`p-3 text-center ${
          error.includes('⚠️') || error.includes('non disponible') 
            ? 'bg-yellow-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {error}
        </div>
      )}

      <div className="flex-1 flex">
        {/* Zone principale - Vidéos */}
        <div className="flex-1 p-4 overflow-auto relative">
          {/* Vue agrandie */}
          {expandedVideo && (() => {
            const expandedData = getExpandedVideoStream();
            if (!expandedData) return null;
            
            return (
              <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
                <div className="relative w-full h-full flex items-center justify-center p-4">
                  <video
                    autoPlay
                    muted={expandedData.isLocal}
                    playsInline
                    className="max-w-full max-h-full object-contain"
                    style={{ width: '100%', height: '100%' }}
                    ref={(video) => {
                      if (video && expandedData.stream) {
                        if (video.srcObject !== expandedData.stream) {
                          video.srcObject = expandedData.stream;
                          video.play().catch(err => {
                            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                              console.warn('[VideoCallPageP2P] Error playing expanded video:', err.name);
                            }
                          });
                        }
                      }
                    }}
                  />
                  <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 px-4 py-2 rounded flex items-center gap-2">
                    {expandedData.isLocal && !isAudioEnabled && <MicOff className="w-4 h-4 text-red-400" />}
                    {expandedData.isLocal && !isVideoEnabled && !isScreenSharing && <VideoOff className="w-4 h-4 text-red-400" />}
                    {expandedData.isLocal && isScreenSharing && <Monitor className="w-4 h-4 text-green-400" />}
                    <span className="text-white font-medium">{expandedData.userName}</span>
                    {expandedData.isLocal && <span className="text-gray-400 text-sm">(Vous)</span>}
                  </div>
                  <button
                    onClick={() => setExpandedVideo(null)}
                    className="absolute top-4 right-4 bg-black bg-opacity-70 hover:bg-opacity-90 p-3 rounded-full transition-all"
                    title="Fermer"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            );
          })()}
          
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${expandedVideo ? 'opacity-30 pointer-events-none' : ''}`}>
            {/* Vidéo locale */}
            <div className="relative bg-gray-800 rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  minHeight: '300px',
                  // Toujours rendre l'élément visible dans le DOM pour permettre l'attachement du stream
                  // Utiliser opacity au lieu de display pour éviter les problèmes de ref
                  opacity: isVideoEnabled && myStream && !isScreenSharing ? 1 : 0,
                  pointerEvents: isVideoEnabled && myStream && !isScreenSharing ? 'auto' : 'none'
                }}
              />
              {/* Écran partagé local */}
              <video
                ref={screenShareRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain border-2 border-green-500"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  minHeight: '300px',
                  display: isScreenSharing ? 'block' : 'none'
                }}
              />
              {(!myStream || (!isVideoEnabled && !isScreenSharing)) && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  {!myStream ? (
                    <div className="text-gray-400">En attente de la caméra...</div>
                  ) : (
                    <div className="text-center">
                      <VideoOff className="w-16 h-16 text-gray-500 mx-auto mb-2" />
                      <div className="text-gray-400">Caméra désactivée</div>
                    </div>
                  )}
                </div>
              )}
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 px-3 py-1 rounded flex items-center gap-2">
                {!isAudioEnabled && <MicOff className="w-4 h-4 text-red-400" />}
                {!isVideoEnabled && !isScreenSharing && <VideoOff className="w-4 h-4 text-red-400" />}
                {isScreenSharing && <Monitor className="w-4 h-4 text-green-400" />}
                <span>{displayName || 'Vous'} {myStream ? '(Vous)' : ''}</span>
              </div>
              {/* Bouton agrandir pour la vidéo locale */}
              {((isVideoEnabled && myStream) || isScreenSharing) && (
                <button
                  onClick={() => toggleExpandVideo('local', isScreenSharing ? 'screen' : 'camera')}
                  className="absolute top-2 right-2 bg-black bg-opacity-70 hover:bg-opacity-90 p-2 rounded transition-all z-10"
                  title={expandedVideo?.id === 'local' && expandedVideo?.type === (isScreenSharing ? 'screen' : 'camera') ? 'Réduire' : 'Agrandir'}
                >
                  {expandedVideo?.id === 'local' && expandedVideo?.type === (isScreenSharing ? 'screen' : 'camera') ? (
                    <Minimize2 className="w-4 h-4 text-white" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-white" />
                  )}
                </button>
              )}
            </div>

            {/* Vidéos distantes */}
            {remoteStreams.map(({ id, stream, userName }) => (
              <div key={id} className="relative bg-gray-800 rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
                <video
                  ref={(video) => {
                    if (video && stream) {
                      // Ne mettre à jour que si le stream a changé
                      if (video.srcObject !== stream) {
                        video.srcObject = stream;
                        remoteVideosRef.current[id] = video;
                        // Forcer la lecture avec gestion d'erreur améliorée
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                          playPromise.catch(err => {
                            // Ignorer les erreurs AbortError (interruption normale)
                            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                              console.warn('[VideoCallPageP2P] Error playing remote video:', err.name);
                            }
                          });
                        }
                      }
                    }
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ width: '100%', height: '100%', minHeight: '300px' }}
                />
                {!stream && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-gray-400">En attente de la vidéo...</div>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 px-3 py-1 rounded flex items-center gap-2">
                  <span>{userName || `User ${id.slice(-4)}`}</span>
                  {isOrganizer && id !== socket?.id && (
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          muteParticipant(id);
                        }}
                        className="p-1 hover:bg-gray-600 rounded transition-colors"
                        title="Mute ce participant"
                      >
                        <MicOff className="w-3 h-3 text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          disableVideoParticipant(id);
                        }}
                        className="p-1 hover:bg-gray-600 rounded transition-colors"
                        title="Désactiver la vidéo"
                      >
                        <VideoOff className="w-3 h-3 text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          kickParticipant(id);
                        }}
                        className="p-1 hover:bg-red-600 rounded transition-colors"
                        title="Expulser ce participant"
                      >
                        <UserX className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  )}
                </div>
                {/* Bouton agrandir pour la vidéo distante */}
                {stream && (
                  <button
                    onClick={() => toggleExpandVideo(id, 'camera')}
                    className="absolute top-2 right-2 bg-black bg-opacity-70 hover:bg-opacity-90 p-2 rounded transition-all z-10"
                    title={expandedVideo?.id === id ? 'Réduire' : 'Agrandir'}
                  >
                    {expandedVideo?.id === id ? (
                      <Minimize2 className="w-4 h-4 text-white" />
                    ) : (
                      <Maximize2 className="w-4 h-4 text-white" />
                    )}
                  </button>
                )}
              </div>
            ))}
            
            {/* Afficher les participants sans stream vidéo */}
            {participants
              .filter(p => !remoteStreams.find(rs => rs.id === p.id) && p.id !== socket?.id)
              .map(({ id, userName }) => (
                <div key={id} className="relative bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '300px' }}>
                  <div className="text-center">
                    <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Users className="w-12 h-12 text-gray-400" />
                    </div>
                    <div className="text-gray-400 flex items-center gap-2">
                      <span>{userName || `User ${id.slice(-4)}`}</span>
                      {isOrganizer && id !== socket?.id && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => muteParticipant(id)}
                            className="p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Mute ce participant"
                          >
                            <MicOff className="w-3 h-3 text-gray-300" />
                          </button>
                          <button
                            onClick={() => disableVideoParticipant(id)}
                            className="p-1 hover:bg-gray-600 rounded transition-colors"
                            title="Désactiver la vidéo"
                          >
                            <VideoOff className="w-3 h-3 text-gray-300" />
                          </button>
                          <button
                            onClick={() => kickParticipant(id)}
                            className="p-1 hover:bg-red-600 rounded transition-colors"
                            title="Expulser ce participant"
                          >
                            <UserX className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Pas de vidéo</div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Chat Window - Fenêtre flottante redimensionnable (style chatbot) */}
        <ChatWindow
          isOpen={chat.chatOpen}
          onClose={() => chat.setChatOpen(false)}
          onOpen={() => chat.setChatOpen(true)}
          messages={chat.messages}
          newMessage={chat.newMessage}
          setNewMessage={chat.setNewMessage}
          sendMessage={chat.sendMessage}
          handleTyping={chat.handleTyping}
          formatTime={chat.formatTime}
          isTyping={chat.isTyping}
          client={null}
          messagesEndRef={chat.messagesEndRef}
          chatInputRef={chat.chatInputRef}
          socketConnected={chat.socketConnected}
          isMinimized={chatMinimized}
          onMinimize={() => setChatMinimized(!chatMinimized)}
        />
      </div>

      {/* Contrôles */}
      <div className="bg-gray-800 p-4 flex justify-center gap-4">
        <button
          onClick={toggleAudio}
          disabled={!myStream}
          className={`p-3 rounded-full transition-colors ${
            isAudioEnabled 
              ? 'bg-gray-700 hover:bg-gray-600' 
              : 'bg-red-600 hover:bg-red-700'
          } ${!myStream ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isAudioEnabled ? 'Désactiver le micro' : 'Activer le micro'}
        >
          {isAudioEnabled ? <Mic size={24} /> : <MicOff size={24} />}
        </button>
        <button
          onClick={toggleVideo}
          disabled={!myStream || isScreenSharing}
          className={`p-3 rounded-full transition-colors ${
            isVideoEnabled 
              ? 'bg-gray-700 hover:bg-gray-600' 
              : 'bg-red-600 hover:bg-red-700'
          } ${!myStream || isScreenSharing ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={forcedVideoOff ? 'Caméra désactivée par l\'organisateur (vous pouvez la réactiver)' : (isScreenSharing ? 'Désactivez le partage d\'écran pour utiliser la caméra' : (isVideoEnabled ? 'Désactiver la caméra' : 'Activer la caméra'))}
        >
          {isVideoEnabled ? <Video size={24} /> : <VideoOff size={24} />}
        </button>
        <button
          onClick={toggleScreenShare}
          disabled={!inMeeting}
          className={`p-3 rounded-full transition-colors ${
            isScreenSharing 
              ? 'bg-green-600 hover:bg-green-700' 
              : 'bg-gray-700 hover:bg-gray-600'
          } ${!inMeeting ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isScreenSharing ? 'Arrêter le partage d\'écran' : 'Partager l\'écran'}
        >
          {isScreenSharing ? <MonitorOff size={24} /> : <Monitor size={24} />}
        </button>
        <button
          onClick={isRecording ? stopRecording : openRecordingDialog}
          disabled={!inMeeting}
          className={`p-3 rounded-full transition-colors relative ${
            isRecording 
              ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
              : 'bg-gray-700 hover:bg-gray-600'
          } ${!inMeeting ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={isRecording ? 'Arrêter l\'enregistrement' : 'Démarrer l\'enregistrement'}
        >
          {isRecording ? <Square size={24} /> : <Circle size={24} />}
          {isRecording && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          )}
        </button>
        {isRecording && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-600 rounded-lg">
            <Circle className="w-4 h-4 animate-pulse" />
            <span className="text-sm font-medium">{formatRecordingTime(recordingTime)}</span>
            <span className="text-xs text-gray-200 ml-2">
              {recordingResolutions[recordingResolution]?.label}
            </span>
          </div>
        )}
        <button
          onClick={() => chat.setChatOpen(!chat.chatOpen)}
          className={`p-3 rounded-full transition-colors relative ${
            chat.chatOpen 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={chat.chatOpen ? 'Fermer le chat' : 'Ouvrir le chat'}
        >
          <MessageSquare size={24} />
          {chat.unreadCount > 0 && !chat.chatOpen && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
              {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={leaveMeeting}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
          title="Quitter la réunion"
        >
          <PhoneOff size={24} />
        </button>
      </div>

      {/* Dialogue de sélection de résolution */}
      {showResolutionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-white">Choisir la résolution d'enregistrement</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Résolution vidéo
              </label>
              <select
                value={recordingResolution}
                onChange={(e) => setRecordingResolution(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                autoFocus
              >
                {Object.entries(recordingResolutions).map(([key, res]) => (
                  <option key={key} value={key}>
                    {res.label} ({res.width}x{res.height}) - {res.bitrate / 1000000} Mbps
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-2">
                Résolution sélectionnée: {recordingResolutions[recordingResolution]?.width}x{recordingResolutions[recordingResolution]?.height}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResolutionDialog(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmAndStartRecording}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
              >
                Démarrer l'enregistrement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallPageP2P;

